import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Children, cloneElement, isValidElement, useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Info, Save, Send, X, ChevronRight } from "lucide-react";
import useOnlineEnrollment from "@/hooks/useOnlineEnrollment";
import { EnrollmentActionStatusMap, EnrollmentStatus, EnrollmentStatusTimeline, getStatusMeta } from "@/utils/enrolmentStatus";
import { studentEnrollmentWorkflowService } from "@/services/studentEnrollmentWorkflowService";
import { adminEnrolmentService } from "@/services/adminEnrolmentService";
import EnrollmentWorkflowTimeline from "@/components/EnrollmentWorkflowTimeline";
import ToastHelper from "@/utils/toastHelper";
import { YES_NO, YES_NO_OPTIONS } from "@/constants/yesNo";
import { getCourseProgressUrl } from "@/components/course-progress/courseProgressNavigation";
import SignaturePad from "@/components/SignaturePad";
const REQ = <span className="text-rose-600 ml-0.5">*</span>;
const isYes = (value) => Number(value) === YES_NO.YES;
const isSelectedYesNo = (value, optionId) => value !== ""
  && value !== null
  && value !== undefined
  && Number(value) === optionId;
const resolveSettingPerm = (...items) => items.find((item) => item && Object.keys(item).length > 0) || {};
const currentDateTimeLocal = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
};

const SECTIONS = [
  { id: "personal", title: "Personal information" },
  { id: "course", title: "Course Details" },
  { id: "language", title: "Learning Details" },
  { id: "employment", title: "Employment Details" },
  { id: "privacy", title: "Privacy" },
  { id: "fees", title: "Fees, Payment Terms and Course Cancellation Terms" },
  { id: "fee-details", title: "Fee Details" },
  { id: "declaration", title: "Declaration" },
  { id: "client-signature", title: "Client Signature" },
  { id: "staff-completion", title: "To be completed by Gippsland Institute of Technology Staff" },
];

const ENROLMENT_SECTION_CODE = Object.freeze({
  PERSONAL_INFORMATION: 1,
  COURSE_DETAILS: 2,
  LEARNING_DETAILS: 3,
  DECLARATION: 4,
  CLIENT_SIGNATURE: 5,
  STAFF_COMPLETION: 6,
  EMPLOYMENT_DETAILS: 7,
});

const ENROLMENT_FIELD_CODE = Object.freeze({
  USI_NUMBER: 1,
  FIRST_NAME: 2,
  LAST_NAME: 3,
  EMAIL: 4,
  MOBILE_NUMBER: 5,
  DATE_OF_BIRTH: 6,
  ADDRESS: 7,
  COUNTRY: 8,
  STATE: 9,
  POST_CODE: 10,
  EMERGENCY_CONTACT_NAME: 11,
  DECLARATION_ACCEPTED: 12,
  GENDER: 13,
  POSTAL_ADDRESS: 14,
  POSTAL_SUBURB: 15,
  POSTAL_STATE: 16,
  POSTAL_COUNTRY: 17,
  POSTAL_POST_CODE: 18,
  INDIGENOUS_STATUS: 19,
  COUNTRY_OF_BIRTH: 20,
  OTHER_LANGUAGE: 21,
  ENGLISH_PROFICIENCY: 22,
  HIGHEST_SCHOOL_LEVEL: 23,
  SCHOOL_COMPLETED_YEAR: 24,
  DISABILITY_TYPE: 25,
  HOW_DID_YOU_HEAR: 26,
  HIGHEST_QUALIFICATION: 27,
  USI_DECLARATION_ACCEPTED: 28,
  CLIENT_NAME: 29,
  CLIENT_SIGNATURE_DATE: 30,
  CLIENT_SIGNATURE: 31,
  SUBURB: 32,
  HOME_PHONE: 33,
  EMERGENCY_RELATIONSHIP: 34,
  EMERGENCY_PHONE: 35,
  EMERGENCY_MOBILE: 36,
  EMPLOYMENT_STATUS: 37,
  OCCUPATION: 38,
  EMPLOYER_NAME: 39,
  EMPLOYER_POSTAL_ADDRESS: 40,
  EMPLOYER_CONTACT_PHONE: 41,
  EMPLOYER_MOBILE: 42,
  EMPLOYER_EMAIL: 43,
  COURSE_ENTRY_REQUIREMENTS_MET: 44,
  SCHOOL_COUNTRY: 45,
  STILL_ATTENDING_SECONDARY_SCHOOL: 46,
  REASONABLE_ADJUSTMENT: 47,
  READING_WRITING_HELP: 48,
  FACE_TO_FACE_SUITABILITY: 49,
  ONLINE_LEARNING_SUITABILITY: 50,
  STAFF_EVIDENCE_SUBMITTED: 51,
  STAFF_ENTRY_REQUIREMENTS_SATISFIED: 52,
  STAFF_OUTCOME_NOTIFIED: 53,
  STAFF_NAME: 54,
  STAFF_SIGNATURE_DATE: 55,
  STAFF_SIGNATURE: 56,
  STUDY_REASON: 57,
});

const SECTION_CODE_TO_ID = Object.freeze({
  [ENROLMENT_SECTION_CODE.PERSONAL_INFORMATION]: "personal",
  [ENROLMENT_SECTION_CODE.COURSE_DETAILS]: "course",
  [ENROLMENT_SECTION_CODE.LEARNING_DETAILS]: "language",
  [ENROLMENT_SECTION_CODE.EMPLOYMENT_DETAILS]: "employment",
  [ENROLMENT_SECTION_CODE.DECLARATION]: "declaration",
  [ENROLMENT_SECTION_CODE.CLIENT_SIGNATURE]: "client-signature",
  [ENROLMENT_SECTION_CODE.STAFF_COMPLETION]: "staff-completion",
});

const VALIDATION_FIELD_ORDER = Object.freeze({
  [ENROLMENT_SECTION_CODE.PERSONAL_INFORMATION]: [
    ENROLMENT_FIELD_CODE.FIRST_NAME,
    ENROLMENT_FIELD_CODE.LAST_NAME,
    ENROLMENT_FIELD_CODE.ADDRESS,
    ENROLMENT_FIELD_CODE.SUBURB,
    ENROLMENT_FIELD_CODE.STATE,
    ENROLMENT_FIELD_CODE.POST_CODE,
    ENROLMENT_FIELD_CODE.COUNTRY,
    ENROLMENT_FIELD_CODE.HOME_PHONE,
    ENROLMENT_FIELD_CODE.MOBILE_NUMBER,
    ENROLMENT_FIELD_CODE.EMAIL,
    ENROLMENT_FIELD_CODE.GENDER,
    ENROLMENT_FIELD_CODE.DATE_OF_BIRTH,
    ENROLMENT_FIELD_CODE.INDIGENOUS_STATUS,
    ENROLMENT_FIELD_CODE.COUNTRY_OF_BIRTH,
    ENROLMENT_FIELD_CODE.USI_NUMBER,
    ENROLMENT_FIELD_CODE.EMERGENCY_CONTACT_NAME,
    ENROLMENT_FIELD_CODE.EMERGENCY_RELATIONSHIP,
    ENROLMENT_FIELD_CODE.EMERGENCY_PHONE,
    ENROLMENT_FIELD_CODE.EMERGENCY_MOBILE,
  ],
  [ENROLMENT_SECTION_CODE.COURSE_DETAILS]: [
    ENROLMENT_FIELD_CODE.HOW_DID_YOU_HEAR,
    ENROLMENT_FIELD_CODE.COURSE_ENTRY_REQUIREMENTS_MET,
  ],
  [ENROLMENT_SECTION_CODE.LEARNING_DETAILS]: [
    ENROLMENT_FIELD_CODE.OTHER_LANGUAGE,
    ENROLMENT_FIELD_CODE.ENGLISH_PROFICIENCY,
    ENROLMENT_FIELD_CODE.SCHOOL_COMPLETED_YEAR,
    ENROLMENT_FIELD_CODE.SCHOOL_COUNTRY,
    ENROLMENT_FIELD_CODE.HIGHEST_SCHOOL_LEVEL,
    ENROLMENT_FIELD_CODE.STILL_ATTENDING_SECONDARY_SCHOOL,
    ENROLMENT_FIELD_CODE.HIGHEST_QUALIFICATION,
    ENROLMENT_FIELD_CODE.DISABILITY_TYPE,
    ENROLMENT_FIELD_CODE.REASONABLE_ADJUSTMENT,
    ENROLMENT_FIELD_CODE.READING_WRITING_HELP,
    ENROLMENT_FIELD_CODE.STUDY_REASON,
    ENROLMENT_FIELD_CODE.FACE_TO_FACE_SUITABILITY,
    ENROLMENT_FIELD_CODE.ONLINE_LEARNING_SUITABILITY,
  ],
  [ENROLMENT_SECTION_CODE.DECLARATION]: [
    ENROLMENT_FIELD_CODE.DECLARATION_ACCEPTED,
    ENROLMENT_FIELD_CODE.USI_DECLARATION_ACCEPTED,
  ],
  [ENROLMENT_SECTION_CODE.CLIENT_SIGNATURE]: [
    ENROLMENT_FIELD_CODE.CLIENT_NAME,
    ENROLMENT_FIELD_CODE.CLIENT_SIGNATURE,
    ENROLMENT_FIELD_CODE.CLIENT_SIGNATURE_DATE,
  ],
  [ENROLMENT_SECTION_CODE.EMPLOYMENT_DETAILS]: [
    ENROLMENT_FIELD_CODE.EMPLOYMENT_STATUS,
    ENROLMENT_FIELD_CODE.OCCUPATION,
    ENROLMENT_FIELD_CODE.EMPLOYER_NAME,
    ENROLMENT_FIELD_CODE.EMPLOYER_POSTAL_ADDRESS,
    ENROLMENT_FIELD_CODE.EMPLOYER_CONTACT_PHONE,
    ENROLMENT_FIELD_CODE.EMPLOYER_MOBILE,
    ENROLMENT_FIELD_CODE.EMPLOYER_EMAIL,
  ],
  [ENROLMENT_SECTION_CODE.STAFF_COMPLETION]: [
    ENROLMENT_FIELD_CODE.STAFF_EVIDENCE_SUBMITTED,
    ENROLMENT_FIELD_CODE.STAFF_ENTRY_REQUIREMENTS_SATISFIED,
    ENROLMENT_FIELD_CODE.STAFF_OUTCOME_NOTIFIED,
    ENROLMENT_FIELD_CODE.STAFF_NAME,
    ENROLMENT_FIELD_CODE.STAFF_SIGNATURE,
    ENROLMENT_FIELD_CODE.STAFF_SIGNATURE_DATE,
  ],
});

const FORM_KEY_TO_FIELD_CODE = Object.freeze({
  usiNumber: ENROLMENT_FIELD_CODE.USI_NUMBER,
  firstName: ENROLMENT_FIELD_CODE.FIRST_NAME,
  lastName: ENROLMENT_FIELD_CODE.LAST_NAME,
  email: ENROLMENT_FIELD_CODE.EMAIL,
  mobileNumber: ENROLMENT_FIELD_CODE.MOBILE_NUMBER,
  dateOfBirth: ENROLMENT_FIELD_CODE.DATE_OF_BIRTH,
  addressLine1: ENROLMENT_FIELD_CODE.ADDRESS,
  suburb: ENROLMENT_FIELD_CODE.SUBURB,
  countryId: ENROLMENT_FIELD_CODE.COUNTRY,
  stateId: ENROLMENT_FIELD_CODE.STATE,
  postCode: ENROLMENT_FIELD_CODE.POST_CODE,
  homePhone: ENROLMENT_FIELD_CODE.HOME_PHONE,
  emergencyContactName: ENROLMENT_FIELD_CODE.EMERGENCY_CONTACT_NAME,
  emergencyContactRelationshipId: ENROLMENT_FIELD_CODE.EMERGENCY_RELATIONSHIP,
  emergencyPhone: ENROLMENT_FIELD_CODE.EMERGENCY_PHONE,
  emergencyMobile: ENROLMENT_FIELD_CODE.EMERGENCY_MOBILE,
  genderId: ENROLMENT_FIELD_CODE.GENDER,
  postalStreetNumber: ENROLMENT_FIELD_CODE.POSTAL_ADDRESS,
  postalStreetName: ENROLMENT_FIELD_CODE.POSTAL_ADDRESS,
  postalSuburb: ENROLMENT_FIELD_CODE.POSTAL_SUBURB,
  postalStateId: ENROLMENT_FIELD_CODE.POSTAL_STATE,
  postalCountryId: ENROLMENT_FIELD_CODE.POSTAL_COUNTRY,
  postalPostCode: ENROLMENT_FIELD_CODE.POSTAL_POST_CODE,
  indigenousStatusId: ENROLMENT_FIELD_CODE.INDIGENOUS_STATUS,
  countryOfBirthId: ENROLMENT_FIELD_CODE.COUNTRY_OF_BIRTH,
  otherLanguage: ENROLMENT_FIELD_CODE.OTHER_LANGUAGE,
  englishProficiencyId: ENROLMENT_FIELD_CODE.ENGLISH_PROFICIENCY,
  highestSchoolLevelId: ENROLMENT_FIELD_CODE.HIGHEST_SCHOOL_LEVEL,
  schoolCompletedYear: ENROLMENT_FIELD_CODE.SCHOOL_COMPLETED_YEAR,
  disabilityTypeIds: ENROLMENT_FIELD_CODE.DISABILITY_TYPE,
  howDidYouHear: ENROLMENT_FIELD_CODE.HOW_DID_YOU_HEAR,
  courseEntryRequirementsMet: ENROLMENT_FIELD_CODE.COURSE_ENTRY_REQUIREMENTS_MET,
  highestQualificationIds: ENROLMENT_FIELD_CODE.HIGHEST_QUALIFICATION,
  schoolCountryId: ENROLMENT_FIELD_CODE.SCHOOL_COUNTRY,
  stillAttendingSecondarySchool: ENROLMENT_FIELD_CODE.STILL_ATTENDING_SECONDARY_SCHOOL,
  reasonableAdjustment: ENROLMENT_FIELD_CODE.REASONABLE_ADJUSTMENT,
  canReadWriteEnglish: ENROLMENT_FIELD_CODE.READING_WRITING_HELP,
  studyReasonIds: ENROLMENT_FIELD_CODE.STUDY_REASON,
  deliveryFaceToFaceSuitable: ENROLMENT_FIELD_CODE.FACE_TO_FACE_SUITABILITY,
  deliveryWorkplaceSuitable: ENROLMENT_FIELD_CODE.ONLINE_LEARNING_SUITABILITY,
  employmentStatusId: ENROLMENT_FIELD_CODE.EMPLOYMENT_STATUS,
  usiDeclarationAccepted: ENROLMENT_FIELD_CODE.USI_DECLARATION_ACCEPTED,
  declarationAccepted: ENROLMENT_FIELD_CODE.DECLARATION_ACCEPTED,
  clientName: ENROLMENT_FIELD_CODE.CLIENT_NAME,
  clientSignatureDate: ENROLMENT_FIELD_CODE.CLIENT_SIGNATURE_DATE,
  staffEvidenceSubmitted: ENROLMENT_FIELD_CODE.STAFF_EVIDENCE_SUBMITTED,
  staffEntryRequirementsSatisfied: ENROLMENT_FIELD_CODE.STAFF_ENTRY_REQUIREMENTS_SATISFIED,
  staffOutcomeNotified: ENROLMENT_FIELD_CODE.STAFF_OUTCOME_NOTIFIED,
  staffName: ENROLMENT_FIELD_CODE.STAFF_NAME,
  staffSignatureDate: ENROLMENT_FIELD_CODE.STAFF_SIGNATURE_DATE,
});

const STATUS_STYLE = Object.freeze({
  Completed: "Completed",
  Pending: "Pending",
  Available: "Available",
  Locked: "Locked",
  Error: "Error",
});

const cbToggle = (arr, v) => arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

const uniqueById = (items = []) => {
  const seen = new Set();
  return items.filter((item) => {
    const id = String(item?.id ?? "");
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

export default function EnrolmentFormStage({ mode = "student", studentBookingId: providedStudentBookingId = null, isAdminReview = false, workflowButtonList = null, onWorkflowButton = null, workflowHistoryLoader = null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const adminReview = isAdminReview || mode === "admin";
  const studentBookingId = providedStudentBookingId || params.studentBookingId || params.id || location.state?.studentBookingId || null;

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    const frameId = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, [studentBookingId]);

  const {
    d,
    set: setFieldValue,
    active,
    setActive,
    submission,
    loading,
    loadError,
    masterData,
    masterLoading,
    saving,
    requiredCount,
    canEdit,
    canSubmit,
    workFlowStatusNo,
    booking,
    settingPerm,
    buttonList,
    save,
    saveAndReturn,
    submit,
    cancel,
    jumpTo,
  } = useOnlineEnrollment(studentBookingId, { isAdmin: adminReview });
  const [showDraftWorkflowHistory, setShowDraftWorkflowHistory] = useState(false);
  const [directWorkflowAction, setDirectWorkflowAction] = useState(null);
  const [submitValidation, setSubmitValidation] = useState(null);
  const [selectedValidationFieldCode, setSelectedValidationFieldCode] = useState(0);
  const [pendingValidationFocus, setPendingValidationFocus] = useState(null);
  const permissions = resolveSettingPerm(settingPerm, d?.settingPerm);
  const showStaffCompletion = permissions.showStaffCompletion === true;
  const invalidFields = useMemo(() => {
    const map = new Map();
    for (const section of submitValidation?.sections || submitValidation?.Sections || []) {
      for (const field of section?.fields || section?.Fields || []) {
        const code = Number(field?.fieldCode ?? field?.FieldCode ?? 0);
        if (!code) continue;
        map.set(code, field?.message || field?.Message || "Required");
      }
    }
    return map;
  }, [submitValidation]);
  const sectionIssueCounts = useMemo(() => {
    const counts = {};
    for (const section of submitValidation?.sections || submitValidation?.Sections || []) {
      const sectionCode = Number(section?.sectionCode ?? section?.SectionCode ?? 0);
      const sectionId = SECTION_CODE_TO_ID[sectionCode];
      if (!sectionId) continue;
      counts[sectionId] = Number(section?.issueCount ?? section?.IssueCount ?? 0);
    }
    return counts;
  }, [submitValidation]);
  const visibleSections = useMemo(() => {
    const sections = showStaffCompletion ? SECTIONS : SECTIONS.filter((section) => section.id !== "staff-completion");
    return sections;
  }, [showStaffCompletion]);
  const lockPersonalInformation = adminReview || permissions.lockPersonalInformation === true;
  const lockCourseDetails = adminReview || permissions.lockCourseDetails === true;
  const lockLearningDetails = adminReview || permissions.lockLearningDetails === true;
  const lockEmploymentDetails = adminReview || permissions.lockEmploymentDetails === true;
  const lockRPL = adminReview || permissions.lockRPL === true;
  const lockPrivacy = adminReview || permissions.lockPrivacy === true;
  const lockFees = adminReview || permissions.lockFees === true;
  const lockFeeDetails = adminReview || permissions.lockFeeDetails === true;
  const lockDeclaration = adminReview || permissions.lockDeclaration === true;
  const lockClientSignature = adminReview || permissions.lockClientSignature === true;
  const lockStaffCompletion = permissions.lockStaffCompletion === true;
  const lockFlags = [lockPersonalInformation, lockCourseDetails, lockLearningDetails, lockEmploymentDetails, lockRPL, lockPrivacy, lockFees, lockFeeDetails, lockDeclaration, lockClientSignature, lockStaffCompletion];
  const studentReadOnly = !adminReview && canEdit === false;
  const formReadOnly = studentReadOnly || (Number(workFlowStatusNo || 0) >= EnrollmentStatus.Submitted && Number(workFlowStatusNo || 0) !== EnrollmentStatus.Returned) || (Object.keys(permissions).some((key) => key.startsWith("lock")) && lockFlags.every(Boolean));
  const showStudentEditActions = !adminReview && canEdit !== false;
  const workflowButtons = (Array.isArray(workflowButtonList) ? workflowButtonList : buttonList).filter((button) => button && button.hideButton !== true && button.HideButton !== true);
  const workflowButtonText = (button) => button.text ?? button.Text ?? button.action ?? button.Action ?? "";
  const workflowButtonNextStatus = (button) => {
    const nextStatus = Number(button?.nextWorkFlowStatusNo ?? button?.NextWorkFlowStatusNo);
    if (Number.isFinite(nextStatus) && nextStatus > 0) return nextStatus;
    return EnrollmentActionStatusMap[workflowButtonText(button)] || 0;
  };
  const workflowButtonNextStatusFromEvent = (event, button) => {
    const datasetValue = event?.currentTarget?.dataset?.workflowStatusNo;
    const datasetStatus = Number(datasetValue);
    if (Number.isFinite(datasetStatus) && datasetStatus > 0) return datasetStatus;
    const legacyDatasetStatus = Number(event?.currentTarget?.dataset?.nextWorkflowStatusNo);
    if (Number.isFinite(legacyDatasetStatus) && legacyDatasetStatus > 0) return legacyDatasetStatus;
    const fallbackStatus = workflowButtonNextStatus(button);
    return Number.isFinite(fallbackStatus) && fallbackStatus > 0 ? fallbackStatus : 0;
  };
  const withWorkflowButtonNextStatus = (button, nextWorkFlowStatusNo) => ({
    ...button,
    nextWorkFlowStatusNo,
    NextWorkFlowStatusNo: nextWorkFlowStatusNo,
  });
  const isApproveWorkflowButton = (button) => workflowButtonNextStatus(button) === EnrollmentStatus.Approved;
  const workflowButtonKey = (button) => `${button.currentWorkFlowStatusNo ?? button.CurrentWorkFlowStatusNo}-${button.nextWorkFlowStatusNo ?? button.NextWorkFlowStatusNo}-${workflowButtonText(button)}`;
  const workflowButtonClass = (button) => {
    const nextStatus = workflowButtonNextStatus(button);
    const baseClass = "inline-flex h-11 min-w-[104px] shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-5 text-sm font-extrabold shadow-sm transition-colors";
    return [EnrollmentStatus.Rejected, EnrollmentStatus.Cancelled, EnrollmentStatus.Expired].includes(nextStatus)
      ? `${baseClass} border border-rose-200 bg-white text-rose-600 hover:bg-rose-50 hover:border-rose-300`
      : [EnrollmentStatus.Approved, EnrollmentStatus.Submitted].includes(nextStatus)
        ? `${baseClass} border border-orange-600 bg-orange-600 text-white hover:bg-orange-700 hover:border-orange-700`
        : `${baseClass} border border-[#183050] bg-white text-[#183050] hover:bg-slate-50`;
  };
  const set = useCallback((key, value) => {
    setFieldValue(key, value);
    const fieldCode = FORM_KEY_TO_FIELD_CODE[key];
    if (!fieldCode) return;
    setSubmitValidation((current) => clearValidationField(current, fieldCode));
    if (selectedValidationFieldCode === fieldCode) {
      setSelectedValidationFieldCode(0);
    }
  }, [selectedValidationFieldCode, setFieldValue]);
  const bookingUnitCode = booking?.unitCode || booking?.courseCode || "CPCWHS1001";
  const bookingUnitName = booking?.courseDescription || booking?.CourseDescription || booking?.unitName || booking?.courseName || "Prepare to Work Safely in the Construction Industry";
  const isMissingStaffChoice = (value) => value === null || value === undefined || value === "";
  const isMissingStaffDate = (value) => {
    if (!value) return true;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) || date.getFullYear() <= 1;
  };
  const validateStaffCompletionForApprove = () => {
    const missingFields = [];
    if (isMissingStaffChoice(d.staffEvidenceSubmitted)) missingFields.push({
      fieldCode: ENROLMENT_FIELD_CODE.STAFF_EVIDENCE_SUBMITTED,
      fieldName: "Evidence submitted",
      message: "Required",
    });
    if (isMissingStaffChoice(d.staffEntryRequirementsSatisfied)) missingFields.push({
      fieldCode: ENROLMENT_FIELD_CODE.STAFF_ENTRY_REQUIREMENTS_SATISFIED,
      fieldName: "Entry requirements satisfied",
      message: "Required",
    });
    if (isMissingStaffChoice(d.staffOutcomeNotified)) missingFields.push({
      fieldCode: ENROLMENT_FIELD_CODE.STAFF_OUTCOME_NOTIFIED,
      fieldName: "Outcome notified",
      message: "Required",
    });
    if (!String(d.staffName || "").trim()) missingFields.push({
      fieldCode: ENROLMENT_FIELD_CODE.STAFF_NAME,
      fieldName: "Staff name",
      message: "Required",
    });
    if (isMissingStaffDate(d.staffSignatureDate)) missingFields.push({
      fieldCode: ENROLMENT_FIELD_CODE.STAFF_SIGNATURE_DATE,
      fieldName: "Date",
      message: "Required",
    });
    if (!String(d.staffSignatureDataUrl || d.staffSignatureViewUrl || "").trim()) missingFields.push({
      fieldCode: ENROLMENT_FIELD_CODE.STAFF_SIGNATURE,
      fieldName: "Staff signature",
      message: "Required",
    });
    if (missingFields.length > 0) {
      const sections = [{
        sectionCode: ENROLMENT_SECTION_CODE.STAFF_COMPLETION,
        sectionName: "Staff Completion",
        statusStyle: STATUS_STYLE.Pending,
        issueCount: missingFields.length,
        fields: missingFields,
      }];
      setSubmitValidation({
        message: "Complete Staff Completion before approving.",
        sections,
      });
      return false;
    }
    setSubmitValidation(null);
    return true;
  };
  const handleWorkflowButton = async (event, button) => {
    const nextWorkFlowStatusNo = workflowButtonNextStatusFromEvent(event, button);
    const buttonWithNextStatus = withWorkflowButtonNextStatus(button, nextWorkFlowStatusNo);

    if (!nextWorkFlowStatusNo) {
      ToastHelper.error("This workflow action is missing its next status.");
      return;
    }

    if (adminReview) {
      const isApprove = isApproveWorkflowButton(buttonWithNextStatus);
      if (isApprove) {
        if (!validateStaffCompletionForApprove()) return;
      }

      const beforeAction = () => save({ silent: true, nextWorkFlowStatusNo });

      if (typeof onWorkflowButton === "function") {
        onWorkflowButton(buttonWithNextStatus, { beforeAction });
        return;
      }

      setDirectWorkflowAction({
        button: buttonWithNextStatus,
        action: workflowButtonText(buttonWithNextStatus),
        nextWorkFlowStatusNo,
        comments: "",
      });
      return;
    }
    const result = await submit({ preventDefault: () => {} });
    if (result?.validation) {
      setSubmitValidation(includeUsiValidation(result.validation));
      return;
    }
    if (result?.success) {
      setSubmitValidation(null);
      navigate(courseProgressUrl, { state: { focusCurrentStep: true } });
    }
  };

  const includeUsiValidation = useCallback((validation) => {
    if (d.usiDeclarationAccepted || !validation) return validation;
    const sections = [...(validation.sections || validation.Sections || [])];
    const declaration = sections.find((section) => Number(section.sectionCode ?? section.SectionCode) === Number(ENROLMENT_SECTION_CODE.DECLARATION));
    if (!declaration) return validation;
    const fields = [...(declaration.fields || declaration.Fields || [])];
    if (fields.some((field) => Number(field.fieldCode ?? field.FieldCode) === Number(ENROLMENT_FIELD_CODE.USI_DECLARATION_ACCEPTED))) return validation;
    fields.push({ fieldCode: ENROLMENT_FIELD_CODE.USI_DECLARATION_ACCEPTED, fieldName: "I have provided my valid Unique Student Identifier (USI)", message: "Required" });
    declaration.fields = fields;
    declaration.Fields = fields;
    declaration.issueCount = fields.length;
    declaration.IssueCount = fields.length;
    return { ...validation, sections, Sections: sections };
  }, [d.usiDeclarationAccepted]);

  const getFieldError = (fieldCode) => invalidFields.get(fieldCode) || "";
  const handleValidationFieldClick = useCallback((sectionCode, fieldCode) => {
    setSubmitValidation(null);
    const sectionId = SECTION_CODE_TO_ID[Number(sectionCode)] || "personal";
    setSelectedValidationFieldCode(Number(fieldCode) || 0);
    setActive(sectionId);
    setPendingValidationFocus({ sectionId, fieldCode: Number(fieldCode) || 0 });
  }, [setActive]);
  const handleValidationClose = useCallback(() => {
    const sections = submitValidation?.sections || submitValidation?.Sections || [];
    const firstSection = sections[0];
    const firstField = firstSection?.fields?.[0] || firstSection?.Fields?.[0];
    const sectionCode = Number(firstSection?.sectionCode ?? firstSection?.SectionCode ?? 0);
    const fieldCode = Number(firstField?.fieldCode ?? firstField?.FieldCode ?? 0);
    setSubmitValidation(null);
    if (sectionCode > 0 && fieldCode > 0) {
      handleValidationFieldClick(sectionCode, fieldCode);
    }
  }, [handleValidationFieldClick, submitValidation]);
  useEffect(() => {
    document.querySelectorAll("[data-validation-selected='true']").forEach((element) => {
      element.removeAttribute("data-validation-selected");
      element.classList.remove("ring-2", "ring-orange-400", "ring-offset-2", "ring-offset-white", "rounded-lg");
    });

    if (!selectedValidationFieldCode) {
      return undefined;
    }

    const target = document.querySelector(`[data-field-code="${selectedValidationFieldCode}"]`);
    if (!target) {
      return undefined;
    }

    target.setAttribute("data-validation-selected", "true");
    target.classList.add("ring-2", "ring-orange-400", "ring-offset-2", "ring-offset-white", "rounded-lg");

    return () => {
      target.removeAttribute("data-validation-selected");
      target.classList.remove("ring-2", "ring-orange-400", "ring-offset-2", "ring-offset-white", "rounded-lg");
    };
  }, [selectedValidationFieldCode]);
  useEffect(() => {
    if (!pendingValidationFocus) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      const target = document.querySelector(`[data-field-code="${pendingValidationFocus.fieldCode}"]`) || document.getElementById(`sec-${pendingValidationFocus.sectionId}`);
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      const input = target?.querySelector("input:not(:disabled), select:not(:disabled), textarea:not(:disabled)");
      if (typeof input?.focus === "function") input.focus({ preventScroll: true });
      else if (typeof target?.focus === "function") target.focus({ preventScroll: true });
      setPendingValidationFocus(null);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [pendingValidationFocus]);
  const handleStudentSubmit = async (event) => {
    const result = await submit(event);
    if (result?.validation) {
      setSubmitValidation(includeUsiValidation(result.validation));
      return;
    }
    if (result?.success) {
      setSubmitValidation(null);
    }
  };

  const confirmDirectWorkflowAction = async () => {
    if (!directWorkflowAction) return;

    const saveResult = await save({ silent: true, nextWorkFlowStatusNo: directWorkflowAction.nextWorkFlowStatusNo });
    if (!saveResult?.success) return;

    const studentEnrollmentId = Number(d.studentEnrollmentId ?? submission?.studentEnrollmentId ?? 0);
    if (!studentEnrollmentId) {
      ToastHelper.error("Student enrolment id is missing for this workflow action.");
      return;
    }

    await adminEnrolmentService.performEnrolmentAction({
      StudentEnrollmentId: studentEnrollmentId,
      Action: directWorkflowAction.action,
      WorkFlowStatusNo: Number(workFlowStatusNo || 0),
      CurrentWorkFlowStatus: {
        WorkFlowStatusNo: Number(workFlowStatusNo || 0),
        WorkFlowStatusName: "",
        NextWorkFlowStatusNo: directWorkflowAction.nextWorkFlowStatusNo,
      },
      AdminComments: directWorkflowAction.comments,
      RejectedReason: directWorkflowAction.action === "Reject" ? directWorkflowAction.comments : "",
    });
    ToastHelper.success("Workflow action completed.");
    setDirectWorkflowAction(null);
    navigate(courseProgressUrl, { state: { focusCurrentStep: true } });
  };

  useEffect(() => {
    if (!d.clientSignatureDate) set("clientSignatureDate", currentDateTimeLocal());
  }, [d.clientSignatureDate, set]);

  useEffect(() => {
    if (typeof window.IntersectionObserver !== "function") return undefined;

    const sections = visibleSections
      .map((section) => document.getElementById(`sec-${section.id}`))
      .filter(Boolean);

    if (sections.length === 0) return undefined;

    const observer = new window.IntersectionObserver((entries) => {
      const visibleSection = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

      if (visibleSection) {
        setActive(visibleSection.target.id.replace(/^sec-/, ""));
      }
    }, {
      rootMargin: "-18% 0px -58% 0px",
      threshold: [0.05, 0.2, 0.4, 0.6],
    });

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [loading, setActive, visibleSections]);

  // Normalize titles so the select can render regardless of backend shape
  const titlesList = (masterData?.titles || []).map((t, idx) => {
    if (!t && typeof t !== "string") return { id: `t-${idx}`, name: String(t) };
    if (typeof t === "string") return { id: t, name: t };
    return { id: t.id ?? t.titleId ?? t.value ?? `t-${idx}`, name: t.name ?? t.title ?? t.text ?? t.label ?? String(t) };
  });

  // Normalize genders similarly so the select renders whatever the API sends
  const gendersList = (masterData?.genders || []).map((g, idx) => {
    if (!g && typeof g !== "string") return { id: `g-${idx}`, name: String(g) };
    if (typeof g === "string") return { id: g, name: g };
    return { id: g.id ?? g.genderId ?? g.value ?? `g-${idx}`, name: g.name ?? g.text ?? g.label ?? g.gender ?? String(g) };
  });

  const statesList = (masterData?.states || []).map((s, idx) => ({ id: s.id ?? `s-${idx}`, name: s.name ?? String(s) }));
  const citiesList = (masterData?.cities || []).map((c, idx) => ({ id: c.id ?? `city-${idx}`, name: c.name ?? String(c) }));
  const addressCountriesList = (masterData?.countries || []).map((c, idx) => ({ id: c.id ?? `country-${idx}`, name: c.name ?? String(c) }));
  const postalStatesList = (masterData?.postalStates || masterData?.states || []).map((s, idx) => ({ id: s.id ?? `ps-${idx}`, name: s.name ?? String(s) }));
  const postalCitiesList = (masterData?.postalCities || masterData?.cities || []).map((c, idx) => ({ id: c.id ?? `pcity-${idx}`, name: c.name ?? String(c) }));
  const countriesList = (masterData?.countriesOfBirth || masterData?.countries || []).map((c, idx) => ({ id: c.id ?? `c-${idx}`, name: c.name ?? String(c) }));
  const postalCountriesList = (masterData?.postalCountries || masterData?.countries || []).map((c, idx) => ({ id: c.id ?? `pc-${idx}`, name: c.name ?? String(c) }));
  const indigenousList = (masterData?.indigenousStatus || []).map((i, idx) => ({ id: i.id ?? `i-${idx}`, name: i.name ?? String(i) }));
  const englishList = (masterData?.englishProficiency || []).map((e, idx) => ({ id: e.id ?? `e-${idx}`, name: e.name ?? String(e) }));
  const studyReasonsList = (masterData?.studyReasons || []).map((r, idx) => ({ id: r.id ?? `r-${idx}`, name: r.name ?? String(r) }));
  const employmentStatusList = (masterData?.employmentStatus || []).map((item, idx) => ({
    id: item?.id ?? item?.Id ?? item?.value ?? item?.Value ?? item?.employmentStatusId ?? item?.EmploymentStatusId ?? `employment-${idx}`,
    name: item?.name ?? item?.Name ?? item?.label ?? item?.Label ?? item?.text ?? item?.Text ?? item?.employmentStatusName ?? item?.EmploymentStatusName ?? String(item),
  }));
  const highestSchoolList = (masterData?.highestSchoolLevels || []).map((h, idx) => ({ id: h.id ?? `h-${idx}`, name: h.name ?? String(h) }));
  const yesNoList = (masterData?.yesNo || []).filter((option) => Number.isInteger(Number(option?.id))).map((option) => {
    const name = option?.name ?? String(option ?? "");
    return { id: Number(option.id), name };
  });
  const yesNoOptions = yesNoList.length > 0 ? yesNoList : YES_NO_OPTIONS;
  const schoolYearsList = (masterData?.schoolCompletedYears || []).map((y, idx) => ({ id: y.id ?? `y-${idx}`, name: y.name ?? String(y) }));
  const emergencyRelationshipsList = (masterData?.emergencyRelationships || []).map((relationship, idx) => ({
    id: relationship.id ?? `relationship-${idx}`,
    name: relationship.name ?? String(relationship),
  }));
  const highestQualificationsList = uniqueById(masterData?.highestQualifications);
  const disabilityTypesList = uniqueById(masterData?.disabilityTypes);
  const courseProgressUrl = location.state?.courseProgressUrl || getCourseProgressUrl(mode, studentBookingId || d.studentBookingId);
  const workflowSubmittedOrLater = Number(workFlowStatusNo || 0) >= EnrollmentStatus.Submitted && Number(workFlowStatusNo || 0) !== EnrollmentStatus.Returned;
  const formProgressPercentage = workflowSubmittedOrLater
    ? 100
    : Math.max(0, Math.min(100, Number(requiredCount.percentage) || 0));
  const formProgressMessage = workflowSubmittedOrLater
    ? "Submitted"
    : formProgressPercentage === 100
    ? "Ready to submit"
    : formProgressPercentage >= 80
    ? "Almost complete"
    : "In progress";

  if (!adminReview && !studentBookingId && !d.studentBookingId) {
    return (
      <div className="mx-auto max-w-2xl py-12" data-testid="enrolment-booking-required">
        <div className="wc-card p-8 text-center">
          <Info className="mx-auto h-10 w-10 text-orange-500" />
          <h1 className="mt-4 text-2xl font-display font-extrabold text-[#0A2540]">Select a course booking first</h1>
          <p className="mt-2 text-sm text-slate-600">
            This enrolment form must be opened from one of your course bookings. Please go to the Courses tab and select the course you want to enrol in.
          </p>
          <Link to="/student/my-courses" className="wc-btn-primary mt-6 inline-flex justify-center" data-testid="go-to-courses">
            Go to Courses
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${adminReview && workflowButtons.length > 0 ? "pb-28" : ""}`} data-testid="online-enrolment-new">
      {studentBookingId && loading ? (
        <div className="wc-card p-6 text-center text-slate-600">Loading enrolment details...</div>
      ) : null}
      {studentBookingId && !loading && loadError ? (
        <div className="wc-card p-6 text-center text-slate-600">No enrolment data found for this booking ID.</div>
      ) : null}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          {!isAdminReview && (
            <Link to={courseProgressUrl} state={{ focusCurrentStep: true }} className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-orange-600" data-testid="back-to-course-progress">
              <ArrowLeft className="h-4 w-4" /> Back to course progress
            </Link>
          )}
          <div className="text-xs uppercase tracking-wide text-orange-600 font-semibold">Refresher FSS Hospitality</div>
          <h1 className="text-2xl font-display font-extrabold text-[#0A2540] mt-1">Complete your enrolment form</h1>
          <p className="text-sm text-slate-500 mt-1">Fields marked with <span className="text-rose-600 font-bold">*</span> are required.</p>
          <div className="mt-4 max-w-xl rounded-xl border border-orange-100 bg-orange-50/80 p-4 shadow-sm" aria-label={`Enrolment form progress ${formProgressPercentage}%`}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-xs font-extrabold uppercase tracking-wide text-orange-700">Enrolment form progress</div>
                <p className="mt-1 text-sm font-semibold text-slate-700">
                  {requiredCount.done} of {requiredCount.total} required fields completed
                </p>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-3xl font-display font-extrabold text-[#EA580C]">{formProgressPercentage}%</div>
                <div className="text-xs font-extrabold uppercase tracking-wide text-orange-700">{formProgressMessage}</div>
              </div>
            </div>
            <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-white ring-1 ring-orange-100">
              <div className="h-full rounded-full bg-orange-500 transition-all" style={{ width: `${formProgressPercentage}%` }} />
            </div>
          </div>
          <StatusProgress status={workflowSubmittedOrLater ? EnrollmentStatus.Submitted : workFlowStatusNo} />
        </div>
        <div className="flex flex-wrap gap-2"><>{d.studentEnrollmentId > 0 && <button type="button" className="wc-btn-outline text-sm" onClick={() => setShowDraftWorkflowHistory((value) => !value)}>{showDraftWorkflowHistory ? "Hide history" : "View workflow history"}</button>}</>{showStudentEditActions && <button type="button" onClick={save} disabled={saving || formReadOnly} className="wc-btn-outline text-sm shrink-0 inline-flex items-center gap-1.5" data-testid="fn-save-top">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save progress"}
        </button>}</div>
      </div>

      {showDraftWorkflowHistory && d.studentEnrollmentId > 0 && <div className="wc-card p-6"><h2 className="mb-4 text-lg font-display font-extrabold text-[#0A2540]">Workflow history</h2><EnrollmentWorkflowTimeline studentEnrollmentId={d.studentEnrollmentId} loadHistory={workflowHistoryLoader || (adminReview ? adminEnrolmentService.getWorkflowHistory : studentEnrollmentWorkflowService.getWorkflowHistory)} isAdmin={adminReview} /></div>}

      <div className="grid items-start gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        {/* Section navigator */}
        <aside className="h-fit self-start lg:sticky lg:top-28">
          <div className="wc-card max-h-[calc(100vh-8rem)] overflow-y-auto p-3">
            <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 px-2 pb-2">Jump to</div>
            {visibleSections.map((s) => {
              const isActive = active === s.id || (s.path && location.pathname === s.path);
              const issueCount = sectionIssueCounts[s.id] || 0;
              const itemClass = `w-full text-left flex items-center justify-between text-sm px-2 py-2 rounded-md transition-colors ${isActive ? "bg-orange-50 text-[#EA580C] font-semibold" : "text-slate-700 hover:bg-slate-50"}`;
              const content = (
                <>
                  <span>{issueCount > 0 ? `${s.title} (${issueCount})` : s.title}</span>
                  <ChevronRight className="w-3.5 h-3.5 opacity-40" />
                </>
              );

              if (s.path) {
                return (
                  <Link key={s.id} to={s.path} className={itemClass} data-testid={`jump-${s.id}`}>
                    {content}
                  </Link>
                );
              }

              return (
                <button key={s.id} onClick={() => jumpTo(s.id)} type="button" className={itemClass} data-testid={`jump-${s.id}`}>
                  {content}
                </button>
              );
            })}
          </div>
        </aside>

        <form onSubmit={handleStudentSubmit} className="space-y-6">
          {/* Personal */}
          <Section id="personal" title="Personal information" locked={formReadOnly || lockPersonalInformation} onFocus={() => setActive("personal")}>
            <Grid>
              <F label={<>Given Name {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.FIRST_NAME} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.FIRST_NAME)} hint="Spelling must match the info you supplied for your USI"><input required className="wc-input" value={d.firstName} onChange={(e) => set("firstName", e.target.value)} data-testid="fn-firstName" /></F>
              <F hidden label="Middle name/s (if applicable)"><input className="wc-input" value={d.middleName} onChange={(e) => set("middleName", e.target.value)} data-testid="fn-middleName" /></F>
              <F label={<>Family name (surname) {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.LAST_NAME} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.LAST_NAME)}><input required className="wc-input" value={d.lastName} onChange={(e) => set("lastName", e.target.value)} data-testid="fn-lastName" /></F>
              <div className="sm:col-span-2 text-xs uppercase font-semibold tracking-wide text-slate-500 mt-2 mb-0">Address</div>
              <F label={<>Address {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.ADDRESS} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.ADDRESS)}><input required autoComplete="address-line1" className="wc-input" value={d.addressLine1} onChange={(e) => set("addressLine1", e.target.value)} data-testid="fn-address" /></F>
              <F label={<>Suburb/Town {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.SUBURB} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.SUBURB)}><input required autoComplete="address-level2" className="wc-input" value={d.suburb} onChange={(e) => set("suburb", e.target.value)} /></F>
              <F label={<>State/Territory {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.STATE} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.STATE)}><select required className="wc-input" value={d.stateId || d.state} onChange={(e) => { const selected = statesList.find((item) => String(item.id) === e.target.value); set("stateId", e.target.value); set("state", selected?.name || ""); }}><option value="">Choose...</option>{statesList.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></F>
              <F label={<>Post Code {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.POST_CODE} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.POST_CODE)}><input required inputMode="numeric" pattern="[0-9]{4}" minLength={4} maxLength={4} className="wc-input" value={d.postCode} onChange={(e) => set("postCode", e.target.value.replace(/\D/g, "").slice(0, 4))} /></F>
              <F label={<>Country {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.COUNTRY} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.COUNTRY)} full><select required className="wc-input" value={d.countryId || ""} onChange={(e) => set("countryId", e.target.value)}><option value="">Choose...</option>{addressCountriesList.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></F>
              <F hidden label="Title">
                  <select required className="wc-input" value={d.titleId || d.title} onChange={(e) => {
                      const val = e.target.value;
                      const sel = (titlesList.find(x => String(x.id) === String(val)) || { name: val });
                      set("titleId", val);
                      set("title", sel.name);
                    }} data-testid="fn-title">
                    <option value="">Choose...</option>
                          {masterLoading ? (
                            <option disabled>Loading...</option>
                          ) : (masterData?.titles && masterData.titles.length > 0) ? (
                            titlesList.map((t) => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))
                          ) : (
                            <option disabled>No data found</option>
                          )}
                  </select>
              </F>
              <F hidden label="Date of Birth"><input type="date" className="wc-input" value={d.dateOfBirth} onChange={(e) => set("dateOfBirth", e.target.value)} /></F>
              <F hidden label="Gender">
                <select required className="wc-input" value={d.genderId || d.gender} onChange={(e) => {
                    const val = e.target.value;
                    // find label
                    const sel = (gendersList.find(x => String(x.id) === String(val)) || { name: val });
                    set("genderId", val);
                    set("gender", sel.name);
                  }} data-testid="fn-gender">
                  <option value="">Choose...</option>
                  {masterLoading ? (
                    <option disabled>Loading...</option>
                  ) : (masterData?.genders && masterData.genders.length > 0) ? (
                    gendersList.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))
                  ) : (
                    <option disabled>No data found</option>
                  )}
                </select>
              </F>
              <F label="Home Phone" hint="Include state prefix (e.g. 03 for VIC, 02 for NSW)"><input className="wc-input" value={d.homePhone} onChange={(e) => set("homePhone", e.target.value)} data-testid="fn-homephone" /></F>
              <F hidden label="Work phone number"><input className="wc-input" value={d.workPhone} onChange={(e) => set("workPhone", e.target.value)} data-testid="fn-workphone" /></F>
              <F label={<>Mobile {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.MOBILE_NUMBER} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.MOBILE_NUMBER)}><input required className="wc-input" value={d.mobileNumber} onChange={(e) => set("mobileNumber", e.target.value)} data-testid="fn-mobileNumber" /></F>
              <F label={<>Email {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.EMAIL} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.EMAIL)}><input required type="email" className="wc-input" value={d.email} onChange={(e) => set("email", e.target.value)} data-testid="fn-email" /></F>
              <F label={<>Male / Female / Prefer not to say {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.GENDER} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.GENDER)}><select required className="wc-input" value={d.genderId || d.gender} onChange={(e) => { const selected = gendersList.find((item) => String(item.id) === e.target.value); set("genderId", e.target.value); set("gender", selected?.name || ""); }}><option value="">Choose...</option>{gendersList.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></F>
              <F label={<>Date of Birth {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.DATE_OF_BIRTH} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.DATE_OF_BIRTH)}><input required type="date" className="wc-input" value={d.dateOfBirth} onChange={(e) => set("dateOfBirth", e.target.value)} /></F>
              <F label={<>Are you of Aboriginal or Torres Strait Islander origin? {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.INDIGENOUS_STATUS} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.INDIGENOUS_STATUS)} full><select required className="wc-input" value={d.indigenousStatusId || d.indigenousStatus} onChange={(e) => { const selected = indigenousList.find((item) => String(item.id) === e.target.value); set("indigenousStatusId", e.target.value); set("indigenousStatus", selected?.name || ""); }}><option value="">Choose...</option>{indigenousList.map((item) => <option key={item.id} value={item.id}>{item.name === "Aboriginal" ? "Yes (Aboriginal)" : item.name === "Torres Strait Islander" ? "Yes (Torres Strait Islander)" : item.name}</option>)}</select></F>
              <F label={<>Country of Birth {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.COUNTRY_OF_BIRTH} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.COUNTRY_OF_BIRTH)}><select required className="wc-input" value={d.countryOfBirthId || d.countryOfBirth} onChange={(e) => { const selected = countriesList.find((item) => String(item.id) === e.target.value); set("countryOfBirthId", e.target.value); set("countryOfBirth", selected?.name || ""); }}><option value="">Choose...</option>{countriesList.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></F>
              <F label="Language Spoken at home"><input className="wc-input" value={d.otherLanguage} onChange={(e) => { set("otherLanguage", e.target.value); set("speakOtherLanguage", e.target.value ? YES_NO.YES : YES_NO.NO); }} /></F>
              <div className="sm:col-span-2 text-xs uppercase font-semibold tracking-wide text-slate-500 mt-2 mb-0">Unique Student Identifier (USI)</div>
              <F label={<>If you already have an unique student identifier please insert it here: {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.USI_NUMBER} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.USI_NUMBER)} full>
                <input required maxLength={10} className="wc-input max-w-sm" value={d.usiNumber} onChange={(e) => set("usiNumber", e.target.value.toUpperCase().trim().slice(0, 10))} placeholder="USI or testing note" data-testid="fn-usi" />
              </F>
              <F hidden label="Preferred contact type">
                <select required className="wc-input" value={d.preferredContactTypeId} onChange={(e) => set("preferredContactTypeId", e.target.value)} data-testid="fn-prefcontact">
                  <option value="">Choose...</option>
                  {(masterData.preferredContactTypes || []).map((option) => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
                </select>
              </F>
            </Grid>

            <div className="text-xs uppercase font-semibold tracking-wide text-slate-500 mt-6 mb-2">Emergency Contact Details</div>
            <Grid>
              <F label={<>Emergency contact {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.EMERGENCY_CONTACT_NAME} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.EMERGENCY_CONTACT_NAME)}><input required className="wc-input" value={d.emergencyContactName} onChange={(e) => set("emergencyContactName", e.target.value)} data-testid="fn-emergency-name" /></F>
              <F label={<>Relationship {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.EMERGENCY_RELATIONSHIP} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.EMERGENCY_RELATIONSHIP)}>
                <select className="wc-input" value={d.emergencyContactRelationshipId || ""} onChange={(e) => {
                  const value = e.target.value;
                  const relationship = emergencyRelationshipsList.find((item) => String(item.id) === value);
                  set("emergencyContactRelationshipId", value);
                  set("emergencyRelationship", relationship?.name || "");
                }} data-testid="fn-emergency-relationship">
                  <option value="">Choose...</option>
                  {masterLoading ? (<option disabled>Loading...</option>) : emergencyRelationshipsList.length > 0 ? (emergencyRelationshipsList.map((relationship) => <option key={relationship.id} value={relationship.id}>{relationship.name}</option>)) : (<option disabled>No data found</option>)}
                </select>
              </F>
              <F label="Daytime telephone"><input className="wc-input" value={d.emergencyPhone} onChange={(e) => set("emergencyPhone", e.target.value)} data-testid="fn-emergency-phone" /></F>
              <F label={<>Mobile {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.EMERGENCY_MOBILE} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.EMERGENCY_MOBILE)}><input className="wc-input" value={d.emergencyMobile} onChange={(e) => set("emergencyMobile", e.target.value)} data-testid="fn-emergency-mobile" /></F>
            </Grid>

            <div className="hidden">Address</div>
            <Grid className="hidden">
              <F hidden label="Address"><input className="wc-input" value={d.addressLine1} onChange={(e) => set("addressLine1", e.target.value)} /></F>
              <F hidden label="Suburb/Town"><input className="wc-input" value={d.suburb} onChange={(e) => set("suburb", e.target.value)} /></F>
              <F hidden label="State/Territory">
                <select required autoComplete="address-level1" className="wc-input" value={d.stateId || d.state} onChange={(e) => {
                    const val = e.target.value;
                    const sel = (statesList.find(x => String(x.id) === String(val)) || { name: val });
                    set("stateId", val);
                    set("state", sel.name);
                  }} data-testid="fn-state">
                  <option value="">Choose...</option>
                  {masterLoading ? (
                    <option disabled>Loading...</option>
                  ) : (statesList.length > 0) ? (
                    statesList.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))
                  ) : (
                    <option disabled>No data found</option>
                  )}
                </select>
              </F>
              <F hidden label="Post Code"><input className="wc-input" value={d.postCode} onChange={(e) => set("postCode", e.target.value)} /></F>
              <F label={<>Country {REQ}</>} full>
                <select required autoComplete="country" className="wc-input" value={d.countryId || ""} onChange={(e) => set("countryId", e.target.value)} data-testid="fn-country">
                  <option value="">Choose...</option>
                  {masterLoading ? (<option disabled>Loading...</option>) : addressCountriesList.length > 0 ? (addressCountriesList.map((country) => <option key={country.id} value={country.id}>{country.name}</option>)) : (<option disabled>No data found</option>)}
                </select>
              </F>
              <F label="City lookup (optional)" full>
                <select className="wc-input" value={d.cityId || ""} onChange={(e) => set("cityId", e.target.value)} data-testid="fn-city">
                  <option value="">Choose...</option>
                  {masterLoading ? (<option disabled>Loading...</option>) : citiesList.length > 0 ? (citiesList.map((city) => <option key={city.id} value={city.id}>{city.name}</option>)) : (<option disabled>No data found</option>)}
                </select>
              </F>
            </Grid>
            {d.sameAsResidential === false && (
              <Grid className="mt-4 sm:grid-cols-3">
                <F label="Postal unit / flat / level (optional)"><input autoComplete="address-line2" className="wc-input" value={d.postalFlatUnitDetails} onChange={(e) => set("postalFlatUnitDetails", e.target.value)} data-testid="fn-postal-flat-unit" /></F>
                <F label="Postal building / property name (optional)"><input className="wc-input" value={d.postalBuildingPropertyName} onChange={(e) => set("postalBuildingPropertyName", e.target.value)} data-testid="fn-postal-building" /></F>
                <F label={<>Postal street number {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.POSTAL_ADDRESS} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.POSTAL_ADDRESS)}><input required className="wc-input" value={d.postalStreetNumber} onChange={(e) => set("postalStreetNumber", e.target.value)} data-testid="fn-postal-street-number" /></F>
                <F label={<>Postal street name and type {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.POSTAL_ADDRESS} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.POSTAL_ADDRESS)}><input required autoComplete="address-line1" className="wc-input" value={d.postalStreetName} onChange={(e) => set("postalStreetName", e.target.value)} data-testid="fn-postal-street-name" /></F>
                <F label={<>Postal suburb / locality {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.POSTAL_SUBURB} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.POSTAL_SUBURB)}><input required autoComplete="address-level2" className="wc-input" value={d.postalSuburb} onChange={(e) => set("postalSuburb", e.target.value)} data-testid="fn-postalsuburb" /></F>
                <F label={<>Postal state / territory {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.POSTAL_STATE} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.POSTAL_STATE)}>
                  <select required autoComplete="address-level1" className="wc-input" value={d.postalStateId || d.postalState} onChange={(e) => {
                      const val = e.target.value;
                      const sel = (postalStatesList.find(x => String(x.id) === String(val)) || { name: val });
                      set("postalStateId", val);
                      set("postalState", sel.name);
                    }} data-testid="fn-postalstate">
                    <option value="">Choose...</option>
                    {masterLoading ? (<option disabled>Loading...</option>) : (postalStatesList.length > 0) ? (postalStatesList.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))) : (<option disabled>No data found</option>)}
                  </select>
                </F>
                <F label={<>Postal postcode {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.POSTAL_POST_CODE} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.POSTAL_POST_CODE)}><input required autoComplete="postal-code" inputMode="numeric" pattern="[0-9]{4}" minLength={4} maxLength={4} className="wc-input" value={d.postalPostCode} onChange={(e) => set("postalPostCode", e.target.value.replace(/\D/g, "").slice(0, 4))} data-testid="fn-postalpc" /></F>
                <F label={<>Postal country {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.POSTAL_COUNTRY} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.POSTAL_COUNTRY)}>
                  <select required autoComplete="country" className="wc-input" value={d.postalCountryId || ""} onChange={(e) => set("postalCountryId", e.target.value)} data-testid="fn-postalcountry">
                    <option value="">Choose...</option>
                    {masterLoading ? (<option disabled>Loading...</option>) : postalCountriesList.length > 0 ? (postalCountriesList.map((country) => <option key={country.id} value={country.id}>{country.name}</option>)) : (<option disabled>No data found</option>)}
                  </select>
                </F>
                <F label="Postal city lookup (optional)">
                  <select className="wc-input" value={d.postalCityId || ""} onChange={(e) => set("postalCityId", e.target.value)} data-testid="fn-postalcity">
                    <option value="">Choose...</option>
                    {masterLoading ? (<option disabled>Loading...</option>) : postalCitiesList.length > 0 ? (postalCitiesList.map((city) => <option key={city.id} value={city.id}>{city.name}</option>)) : (<option disabled>No data found</option>)}
                  </select>
                </F>
              </Grid>
            )}
          </Section>

          <Section id="course" title="Course Details" locked={formReadOnly || lockCourseDetails} onFocus={() => setActive("course")}>
            <p className="mb-4 text-sm text-slate-600">Indicate the name and code of the course or unit of competency for which you are applying.</p>
            <Grid>
              <F label="Unit code"><input readOnly className="wc-input bg-slate-50" value={bookingUnitCode} /></F>
              <F label="Unit name"><input readOnly className="wc-input bg-slate-50" value={bookingUnitName} /></F>
              <F label={<>How did you hear about the course? {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.HOW_DID_YOU_HEAR} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.HOW_DID_YOU_HEAR)} full><select required className="wc-input" value={d.howDidYouHear} onChange={(e) => set("howDidYouHear", e.target.value)}><option value="">Choose...</option><option>Employer</option><option>colleague</option><option>internet</option><option>flyer</option><option>word of mouth</option><option>job network</option><option>yellow pages</option><option>advertisement</option><option>training.gov.au</option></select></F>
              <F label={<>Do you satisfy the course entry requirements {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.COURSE_ENTRY_REQUIREMENTS_MET} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.COURSE_ENTRY_REQUIREMENTS_MET)}><div className="flex gap-4">{YES_NO_OPTIONS.map((option) => <label key={option.id} className="inline-flex items-center gap-2 text-sm"><input type="radio" name="courseEntryRequirementsMet" checked={isSelectedYesNo(d.courseEntryRequirementsMet, option.id)} onChange={() => set("courseEntryRequirementsMet", option.id)} />{option.name}</label>)}</div></F>
            </Grid>
          </Section>

          {/* Language & cultural */}
          <section id="sec-language" onFocus={() => setActive("language")} className="wc-card p-6 scroll-mt-24 flex flex-col" data-testid="section-language">
            <div className="flex items-center gap-2 border-b border-slate-200 pb-4 mb-5"><div className="w-1 h-6 bg-orange-500 rounded" /><h2 className="text-lg font-bold text-[#0A2540]">Learning Details</h2></div>
            <Grid>
              <F hidden label="Country of Birth">
                <select required className="wc-input" value={d.countryOfBirthId || d.countryOfBirth} onChange={(e) => {
                    const val = e.target.value;
                    const sel = (countriesList.find(x => String(x.id) === String(val)) || { name: val });
                    set("countryOfBirthId", val);
                    set("countryOfBirth", sel.name);
                  }} data-testid="fn-borncountry">
                  <option value="">Choose...</option>
                  {masterLoading ? (<option disabled>Loading...</option>) : (countriesList.length > 0) ? (countriesList.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))) : (<option disabled>No data found</option>)}
                </select>
              </F>
              <F hidden label="Town/City"><input className="wc-input" value={d.townOfBirth} onChange={(e) => set("townOfBirth", e.target.value)} /></F>
              <F hidden label="Are you an Australian citizen?">
                <div className="flex gap-4">
                  {YES_NO_OPTIONS.map((option) => <label key={option.id} className="inline-flex items-center gap-2 text-sm"><input type="radio" name="aus" checked={isSelectedYesNo(d.australianCitizen, option.id)} onChange={() => set("australianCitizen", option.id)} data-testid={`fn-aus-${option.name.toLowerCase()}`} />{option.name}</label>)}
                </div>
              </F>
              <F hidden label="Are you of Aboriginal or Torres Strait Islander origin?">
                <select required className="wc-input" value={d.indigenousStatusId || d.indigenousStatus} onChange={(e) => {
                      const val = e.target.value;
                      const sel = (indigenousList.find(x => String(x.id) === String(val)) || { name: val });
                      set("indigenousStatusId", val);
                      set("indigenousStatus", sel.name);
                    }} data-testid="fn-aboriginal">
                  <option value="">Choose...</option>
                  {masterLoading ? (
                    <option disabled>Loading...</option>
                  ) : indigenousList.length > 0 ? (
                    indigenousList.map((i) => (<option key={i.id} value={i.id}>{i.name}</option>))
                  ) : (
                    <option disabled>No data found</option>
                  )}
                </select>
              </F>
              <F hidden label="Language Spoken at home">
                <select required className="wc-input" value={d.speakOtherLanguage} onChange={(e) => set("speakOtherLanguage", e.target.value)} data-testid="fn-speaks">
                  <option value="">Choose...</option>
                  {yesNoOptions.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
                </select>
              </F>
              {isYes(d.speakOtherLanguage) && (
                <F hidden label="Language Spoken at home"><textarea rows={2} className="wc-input resize-none" value={d.otherLanguage} onChange={(e) => set("otherLanguage", e.target.value)} /></F>
              )}
              <F label={<>How well do you speak English? {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.ENGLISH_PROFICIENCY} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.ENGLISH_PROFICIENCY)} full>
                <select required disabled={formReadOnly || lockLearningDetails} className="wc-input max-w-xs" value={d.englishProficiencyId || d.englishProficiency} onChange={(e) => {
                      const val = e.target.value;
                      const sel = (englishList.find(x => String(x.id) === String(val)) || { name: val });
                      set("englishProficiencyId", val);
                      set("englishProficiency", sel.name);
                    }} data-testid="fn-english">
                  <option value="">Choose...</option>
                  {masterLoading ? (
                    <option disabled>Loading...</option>
                  ) : englishList.length > 0 ? (
                    englishList.map((e) => (<option key={e.id} value={e.id}>{e.name}</option>))
                  ) : (
                    <option disabled>No data found</option>
                  )}
                </select>
              </F>
            </Grid>

          {/* Schooling */}
          <Section id="schooling" merge order={1} locked={formReadOnly || lockLearningDetails} onFocus={() => setActive("schooling")}>
            <Grid>
              <F hidden label="Have you completed Year 12 of secondary school?">
                <select required className="wc-input" value={d.completedYear12} onChange={(e) => set("completedYear12", e.target.value)} data-testid="fn-completedYear12">
                  <option value="">Choose...</option>
                  {yesNoOptions.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
                </select>
              </F>
              <F label={<>What is your highest completed school year? (Tick one) {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.HIGHEST_SCHOOL_LEVEL} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.HIGHEST_SCHOOL_LEVEL)} order={3}>
                <select required className="wc-input" value={d.highestSchoolLevelId || d.highestSchoolLevel} onChange={(e) => {
                      const val = e.target.value;
                      const sel = (highestSchoolList.find(x => String(x.id) === String(val)) || { name: val });
                      set("highestSchoolLevelId", val);
                      set("highestSchoolLevel", sel.name);
                    }} data-testid="fn-highestyr">
                  <option value="">Choose...</option>
                  {masterLoading ? (
                    <option disabled>Loading...</option>
                  ) : highestSchoolList.length > 0 ? (
                    highestSchoolList.map((h) => (<option key={h.id} value={h.id}>{h.name}</option>))
                  ) : (
                    <option disabled>No data found</option>
                  )}
                </select>
              </F>
              <F label={<>Year school completed (e.g. 1998) {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.SCHOOL_COMPLETED_YEAR} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.SCHOOL_COMPLETED_YEAR)} order={1}><input required inputMode="numeric" maxLength={4} className="wc-input" value={d.schoolCompletedYear} onChange={(e) => set("schoolCompletedYear", e.target.value)} placeholder="e.g. 1998" data-testid="fn-yrcomp" /></F>
              <F label={<>Country {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.SCHOOL_COUNTRY} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.SCHOOL_COUNTRY)} order={2}>
                <select className="wc-input" value={d.schoolCountryId || ""} onChange={(e) => set("schoolCountryId", e.target.value)}>
                  <option value="">Choose...</option>
                  {addressCountriesList.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </F>
              <F label={<>Are you still attending secondary school? {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.STILL_ATTENDING_SECONDARY_SCHOOL} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.STILL_ATTENDING_SECONDARY_SCHOOL)} order={4}><YesNoRadio name="stillAttendingSecondarySchool" value={d.stillAttendingSecondarySchool} disabled={lockLearningDetails} onChange={(value) => set("stillAttendingSecondarySchool", value)} /></F>
              <F hidden label="Are you still attending secondary school?" order={4}>
                <select className="wc-input" value={d.stillAttendingSecondarySchool} onChange={(e) => set("stillAttendingSecondarySchool", e.target.value)}><option value="">Choose...</option>{yesNoOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select>
              </F>
            </Grid>
          </Section>

          {/* Education */}
          <Section id="education" merge order={2} locked={formReadOnly || lockLearningDetails} onFocus={() => setActive("education")}>
            <F label={<>Have you successfully completed any of the following qualifications? {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.HIGHEST_QUALIFICATION} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.HIGHEST_QUALIFICATION)} full>
              <div className="flex gap-4">{YES_NO_OPTIONS.map((option) => <label key={option.id} className="inline-flex items-center gap-2 text-sm"><input type="radio" name="ableToCompleteQualification" checked={isSelectedYesNo(d.ableToCompleteQualification, option.id)} onChange={() => set("ableToCompleteQualification", option.id)} />{option.name}</label>)}</div>
            </F>
            <div className="mb-3 text-sm text-slate-600">If you have completed qualifications since leaving school, which option/s best describes?</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {highestQualificationsList.map((qualification) => {
                const qualificationId = String(qualification.id);
                const disabled = !isYes(d.ableToCompleteQualification);
                return (
                <label key={qualificationId} className={`flex items-center gap-2 p-2.5 rounded-md text-sm ${disabled ? "cursor-not-allowed opacity-60 bg-slate-50" : "hover:bg-slate-50"}`}>
                  <input type="checkbox" disabled={disabled} checked={d.highestQualificationIds.includes(qualificationId)} onChange={() => set("highestQualificationIds", cbToggle(d.highestQualificationIds, qualificationId))} data-testid={`fn-cert-${qualificationId}`} />
                  <span>{qualification.name}</span>
                </label>
              );})}
            </div>
            <Grid className="mt-4">
              <F hidden label={<>Have you successfully completed any of the following qualifications? {REQ}</>}>
                <select required className="wc-input" value={d.ableToCompleteQualification} onChange={(e) => set("ableToCompleteQualification", e.target.value)} data-testid="fn-ablecomplete">
                  <option value="">Choose...</option>
                  {yesNoOptions.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
                </select>
              </F>
            </Grid>
          </Section>

          {/* Disability */}
          <Section id="disability" merge order={3} locked={formReadOnly || lockLearningDetails} onFocus={() => setActive("disability")}>
            <div className="border-t border-slate-200 pt-5">
              <span className="wc-label">Do you have a disability, impairment or long term condition? {REQ}</span>
              <div className="mt-2 flex gap-4">
                {YES_NO_OPTIONS.map((option) => <label key={option.id} className="inline-flex items-center gap-2 text-sm"><input type="radio" name="dis" checked={isSelectedYesNo(d.disabilityFlag, option.id)} onChange={() => set("disabilityFlag", option.id)} data-testid={`fn-dis-${option.name.toLowerCase()}`} />{option.name}</label>)}
              </div>
            </div>
            <>
                <div className="mt-4 text-sm text-slate-600 mb-2">If you indicated the presence of a disability, impairment or long-term condition, please select the area(s) in the following list:</div>
                <div className={`grid sm:grid-cols-4 gap-2 ${getFieldError(ENROLMENT_FIELD_CODE.DISABILITY_TYPE) ? "rounded-lg border border-amber-200 bg-amber-50/60 p-3" : ""}`} data-field-code={ENROLMENT_FIELD_CODE.DISABILITY_TYPE}>
                  {disabilityTypesList.map((disability) => {
                    const disabilityId = String(disability.id);
                    return (
                    <label key={disabilityId} className="flex items-center gap-2 p-2.5 rounded-md hover:bg-slate-50 text-sm">
                      <input type="checkbox" checked={d.disabilityTypeIds.includes(disabilityId)} disabled={!isYes(d.disabilityFlag)} onChange={() => set("disabilityTypeIds", cbToggle(d.disabilityTypeIds, disabilityId))} data-testid={`fn-disarea-${disabilityId}`} />
                      <span>{disability.name}</span>
                    </label>
                  );})}
                </div>
                <F hidden label="Additional support required (optional)" full>
                  <textarea rows={3} className="wc-input resize-none" value={d.disabilitySupportRequired} onChange={(e) => set("disabilitySupportRequired", e.target.value)} placeholder="Let us know what adjustments would help you succeed..." data-testid="fn-dissupport" />
                </F>
            </>
          </Section>

          {/* Study reason */}
          <Section id="study" merge order={4} locked={formReadOnly || lockLearningDetails} onFocus={() => setActive("study")}>
            <Grid>
              <F hidden label="How did you hear about the course?">
                <select required className="wc-input" value={d.howDidYouHear} onChange={(e) => set("howDidYouHear", e.target.value)} data-testid="fn-hearabout">
                  <option value="">Choose...</option><option>Employer</option><option>colleague</option><option>internet</option><option>flyer</option><option>word of mouth</option><option>job network</option><option>yellow pages</option><option>advertisement</option><option>training.gov.au</option>
                </select>
              </F>
              <F label={<>Is there anything that may prevent you from successfully completing the training course? {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.REASONABLE_ADJUSTMENT} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.REASONABLE_ADJUSTMENT)} order={1}><YesNoRadio name="reasonableAdjustment" value={d.reasonableAdjustment} disabled={lockLearningDetails} onChange={(value) => set("reasonableAdjustment", value)} /></F>
              <F hidden label="Is there anything that may prevent you from successfully completing the training course?" order={1}>
                <select className="wc-input" value={d.reasonableAdjustment} onChange={(e) => set("reasonableAdjustment", e.target.value)} data-testid="fn-adjust">
                  <option value="">Choose...</option>
                  {yesNoOptions.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
                </select>
              </F>
              <div className="sm:col-span-2 text-sm text-slate-600" style={{ order: 2 }}>If yes please submit details with this application.</div>
              <F label={<>Do you need help with reading and writing or maths? {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.READING_WRITING_HELP} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.READING_WRITING_HELP)} order={3}><YesNoRadio name="canReadWriteEnglish" value={d.canReadWriteEnglish} disabled={lockLearningDetails} onChange={(value) => set("canReadWriteEnglish", value)} /></F>
              <F hidden label="Do you need help with reading and writing or maths?" order={3}>
                <select className="wc-input" value={d.canReadWriteEnglish} onChange={(e) => set("canReadWriteEnglish", e.target.value)} data-testid="fn-writeen">
                  <option value="">Choose...</option>
                  {yesNoOptions.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
                </select>
              </F>
            </Grid>
            <div
              className={`mt-5 border-t pt-5 ${getFieldError(ENROLMENT_FIELD_CODE.STUDY_REASON) ? "rounded-lg border border-amber-200 bg-amber-50/60 p-3" : "border-slate-200"}`}
              data-field-code={ENROLMENT_FIELD_CODE.STUDY_REASON}
            >
              <div className="wc-label">For what reason/s, as listed below, do you wish to complete this course?</div>
              {getFieldError(ENROLMENT_FIELD_CODE.STUDY_REASON) && <p className="mt-1 text-xs font-medium text-amber-700">{getFieldError(ENROLMENT_FIELD_CODE.STUDY_REASON)}</p>}
              <div className="mt-2 grid sm:grid-cols-3 gap-2">{studyReasonsList.map((reason) => { const reasonId = String(reason.id); return <label key={reasonId} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={d.studyReasonIds.includes(reasonId)} onChange={() => set("studyReasonIds", cbToggle(d.studyReasonIds, reasonId))} />{reason.name}</label>; })}</div>
            </div>
            <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <h3 className="font-semibold text-[#0A2540]">Course delivery and assessment methods</h3>
              <p className="mt-1 text-sm text-slate-600">Indicate whether the delivery and assessment methods are appropriate for your learning requirements.</p>
              <Grid className="mt-4 sm:grid-cols-3">
              <F label={<>Face to face (6 Hours) {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.FACE_TO_FACE_SUITABILITY} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.FACE_TO_FACE_SUITABILITY)}><div className="flex gap-4">{YES_NO_OPTIONS.map((option) => <label key={option.id} className="inline-flex items-center gap-2 text-sm"><input type="radio" name="deliveryFaceToFaceSuitable" checked={isSelectedYesNo(d.deliveryFaceToFaceSuitable, option.id)} onChange={() => set("deliveryFaceToFaceSuitable", option.id)} />{option.name}</label>)}</div></F>
              <F label={<>Online learning (6 Hours) {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.ONLINE_LEARNING_SUITABILITY} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.ONLINE_LEARNING_SUITABILITY)}><div className="flex gap-4">{YES_NO_OPTIONS.map((option) => <label key={option.id} className="inline-flex items-center gap-2 text-sm"><input type="radio" name="deliveryWorkplaceSuitable" checked={isSelectedYesNo(d.deliveryWorkplaceSuitable, option.id)} onChange={() => set("deliveryWorkplaceSuitable", option.id)} />{option.name}</label>)}</div></F>
                <F label="If you have answered No to the questions above on delivery and assessment methods, please provide the reason below:" full><textarea rows={3} className="wc-input resize-none" value={d.deliveryAssessmentRemarks} onChange={(e) => set("deliveryAssessmentRemarks", e.target.value)} /></F>
              </Grid>
            </div>
          </Section>
          </section>

          {/* Employment */}
          <Section id="employment" title="Employment Details" locked={formReadOnly || lockEmploymentDetails} onFocus={() => setActive("employment")}>
            <Grid>
              <F hidden label={<>Do you hold a health-care card, pensioner card or VEVO card? {REQ}</>}><YesNoRadio name="hasConcessionCard" value={d.hasConcessionCard} disabled={lockEmploymentDetails} onChange={(value) => set("hasConcessionCard", value)} /></F>
              <F hidden label={<>Do you hold a health-care card, pensioner card or VEVO card? {REQ}</>}>
                <select required className="wc-input" value={d.hasConcessionCard} onChange={(e) => set("hasConcessionCard", e.target.value)} data-testid="fn-conccard">
                  <option value="">Choose...</option>
                  {yesNoOptions.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
                </select>
              </F>
              {isYes(d.hasConcessionCard) && (
                <F label="Concession card number"><input className="wc-input" value={d.concessionCardNumber} onChange={(e) => set("concessionCardNumber", e.target.value)} data-testid="fn-concno" /></F>
              )}
              <F label={<>Which situation, as listed below, best describes your current employment status? {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.EMPLOYMENT_STATUS} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.EMPLOYMENT_STATUS)} full><div className="grid sm:grid-cols-2 gap-2">{employmentStatusList.map((item) => <label key={item.id} className="flex items-center gap-2 text-sm"><input type="radio" name="employmentStatusId" checked={String(d.employmentStatusId || "") === String(item.id)} onChange={() => set("employmentStatusId", item.id)} />{item.name}</label>)}</div></F>
              <F label="Occupation"><input className="wc-input" value={d.occupation} onChange={(e) => set("occupation", e.target.value)} data-testid="fn-jobtitle" /></F>
              <F hidden label="Employment start date"><input type="date" className="wc-input" value={d.employmentStartDate} onChange={(e) => set("employmentStartDate", e.target.value)} data-testid="fn-jobstart" /></F>
              <F hidden label="Average hours worked per week"><input type="number" inputMode="numeric" min="0" step="1" className="wc-input" value={d.workingHoursPerWeek} onChange={(e) => set("workingHoursPerWeek", e.target.value.replace(/\D/g, ""))} data-testid="fn-jobhours" /></F>
              <F label="Business Name"><input className="wc-input" value={d.employerName} onChange={(e) => set("employerName", e.target.value)} data-testid="fn-jobemployer" /></F>
            </Grid>
          </Section>

          {/* LLN */}
          <Section id="lln" hidden locked={studentReadOnly || lockLearningDetails} onFocus={() => setActive("lln")}>
            <F label="Please describe any learning support you may need for Language, Literacy or Numeracy (optional)" full>
              <textarea rows={3} className="wc-input resize-none" value={d.llnSupportRequired} onChange={(e) => set("llnSupportRequired", e.target.value)} data-testid="fn-lln" />
            </F>
          </Section>

          <Section id="privacy" title="Privacy" locked={formReadOnly || lockPrivacy} onFocus={() => setActive("privacy")}>
            <div className="space-y-3 text-sm leading-relaxed text-slate-700">
              <p>Gippsland Institute of Technology respects client's privacy rights and operates in compliance with the National VET Data Policy, Privacy Act 1988 and Australian Privacy Principles.</p>
              <p>Gippsland Institute of Technology will treat client personal information confidentially and will not disclose details to a third party without the client's prior written consent, except where required to comply with the National Vocational Education and Training Regulator Act 2011 and to supply client data to ASQA, NCVER and other regulatory bodies or a court of law.</p>
              <p>Refer to the Privacy and use of personal information section of the Client Information Handbook for details of how your personal data is shared and used.</p>
            </div>
          </Section>

          <Section id="fees" title="Fees, Payment Terms and Course Cancellation Terms" locked={studentReadOnly || lockFees} onFocus={() => setActive("fees")}>
            <div className="space-y-7 text-sm text-slate-800">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[680px] border-collapse border border-slate-200">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      <th className="w-2/3 border border-slate-200 px-3 py-2 font-semibold">Course withdrawal/ cancellation period</th>
                      <th className="border border-slate-200 px-3 py-2 font-semibold">Terms</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-slate-200 px-3 py-2 align-top">More than 28 days prior to commencement of the course all fees less the application fee is refunded</td>
                      <td className="border border-slate-200 px-3 py-2 align-top">Full refund minus enrolment fee</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-200 px-3 py-2 align-top">If an enrolment is cancelled within 28 days of commencement of the course or the client does not commence on the agreed date, or withdraws from the course once it has commenced there</td>
                      <td className="border border-slate-200 px-3 py-2 align-top">No refund of fees paid to date, unless extenuating circumstances apply. Refer to Fee and refund policy and procedure for further details.</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-200 px-3 py-2 align-top">Withdrawal after course start date</td>
                      <td className="border border-slate-200 px-3 py-2 align-top">No refund provided</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-200 px-3 py-2 align-top">Gippsland Institute of Technology fails to deliver the course within 10 working days of the agreed date</td>
                      <td className="border border-slate-200 px-3 py-2 align-top">Client is offered the next course start date or full refund of fees paid to date. Gippsland Institute of Technology is liable for its own associated costs.</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-200 px-3 py-2 align-top">Gippsland Institute of Technology fails to deliver the agreed services after the fee has been paid.</td>
                      <td className="border border-slate-200 px-3 py-2 align-top">Full refund of fees paid to date.</td>
                    </tr>
                    <tr>
                      <td colSpan={2} className="border border-slate-200 px-3 py-2 align-top">If there are extenuating circumstances leading to the client's withdrawal Gippsland Institute of Technology will review the circumstances along with supporting evidence and may to offer the course at the next intake. Fees paid will be transferred to the next available course in such circumstances. Assessment of each situation is at the CEO's discretion.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Section>

          <Section id="fee-details" title="Fee Details" locked={studentReadOnly || lockFeeDetails}>
            <p className="text-sm text-slate-700"><span className="font-semibold">Course fee:</span> Refer to your confirmed course booking and payment record.</p>
            <p className="mt-4 text-sm leading-relaxed text-slate-700">Prior to completing this enrolment form it is important that you read the Client Information Handbook, which outlines important information about your training course and identifies your and Gippsland Institute of Technology's rights and responsibilities. Clients are encouraged to contact the Training Manager to seek clarification on any item.</p>
          </Section>

          {/* Declaration */}
          <Section id="declaration" title="Declaration" locked={formReadOnly || lockDeclaration} onFocus={() => setActive("declaration")}>
            <p className="mb-4 text-sm font-semibold text-slate-700">Please read this section carefully before signing and submitting the application.</p>
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 leading-relaxed">
              <ul className="list-disc pl-5 space-y-1">
                <li>I have read and understood the information in the Client Information Handbook</li>
                <li>I understand how to access Gippsland Institute of Technology services and information as described in the Client Information Handbook</li>
                <li>I understand that access to my academic records is provided free of charge. (if I have paid all fees relating to the record I wish to access).</li>
                <li>I declare that the information provided by me in this application is true, correct and complete at this time.</li>
                <li>I acknowledge that providing false, misleading or inaccurate information may affect the acceptance of this application and/or the continued provision of training and assessment services</li>
                <li>I have read and understood Gippsland Institute of Technology fees and refund policy and procedure.</li>
                <li>I acknowledge that all fees are payable in full on course commencement or the commencement of the term that the fees relate to</li>
                <li>I agree to abide by Gippsland Institute of Technology Code of Conduct and Policies and Procedures.</li>
                <li className="text-slate-700">I understand that if I breach the Gippsland Institute of Technology Code of conduct, my enrolment at Gippsland Institute of Technology may be cancelled.</li>
                <li>I understand my rights when undertaking a course with Gippsland Institute of Technology including the right to access the Complaints and Appeals Policy. I also understand I have the right to access Australian Consumer Protection Law.</li>
                <li>I acknowledge that the information that I provide to Gippsland Institute of Technology may be made available to Commonwealth and State agencies through its obligations to comply with the National Vocational Education and Training Regulator Act 2011.</li>
              </ul>
            </div>
            <label className={`mt-4 flex items-start gap-3 p-3 bg-white border rounded-lg cursor-pointer hover:border-orange-400 ${getFieldError(ENROLMENT_FIELD_CODE.USI_DECLARATION_ACCEPTED) ? "border-amber-300 bg-amber-50/60" : "border-slate-200"}`} data-field-code={ENROLMENT_FIELD_CODE.USI_DECLARATION_ACCEPTED}>
              <input type="checkbox" className="mt-1" checked={d.usiDeclarationAccepted} onChange={(e) => set("usiDeclarationAccepted", e.target.checked)} data-testid="fn-decl-usi" />
              <span className="text-sm text-slate-700">I have provided my valid Unique Student Identifier (USI) {REQ}</span>
            </label>
            {getFieldError(ENROLMENT_FIELD_CODE.USI_DECLARATION_ACCEPTED) && <div className="mt-2 text-xs font-semibold text-amber-700">{getFieldError(ENROLMENT_FIELD_CODE.USI_DECLARATION_ACCEPTED)}</div>}
            <label className={`mt-2 flex items-start gap-3 p-3 bg-white border rounded-lg cursor-pointer hover:border-orange-400 ${getFieldError(ENROLMENT_FIELD_CODE.DECLARATION_ACCEPTED) ? "border-amber-300 bg-amber-50/60" : "border-slate-200"}`} data-field-code={ENROLMENT_FIELD_CODE.DECLARATION_ACCEPTED}>
              <input type="checkbox" className="mt-1" checked={d.declarationAccepted} onChange={(e) => set("declarationAccepted", e.target.checked)} data-testid="fn-decl-terms" />
              <span className="text-sm text-slate-700">I accept the terms and conditions described in the privacy notice above {REQ}</span>
            </label>
            {getFieldError(ENROLMENT_FIELD_CODE.DECLARATION_ACCEPTED) && <div className="mt-2 text-xs font-semibold text-amber-700">{getFieldError(ENROLMENT_FIELD_CODE.DECLARATION_ACCEPTED)}</div>}
          </Section>

          <Section id="client-signature" title="Client Signature" locked={studentReadOnly || lockClientSignature} onFocus={() => setActive("client-signature")}>
            <Grid>
              <F label={<>Client name {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.CLIENT_NAME} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.CLIENT_NAME)}><input className="wc-input" value={d.clientName || [d.firstName, d.lastName].filter(Boolean).join(" ")} onChange={(e) => set("clientName", e.target.value)} /></F>
              <F label={<>Client signature {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.CLIENT_SIGNATURE} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.CLIENT_SIGNATURE)}>
                <SignaturePad
                  value={d.clientSignatureDataUrl || ""}
                  existingImageUrl={d.clientSignatureViewUrl || ""}
                  disabled={studentReadOnly || lockClientSignature}
                  onChange={(value) => {
                    set("clientSignatureDataUrl", value);
                    set("clientSignatureText", value ? "Signed" : "");
                    set("removeClientSignature", value === "" && Boolean(d.clientSignatureDocumentId));
                  }}
                />
              </F>
              <F label={<>Date {REQ}</>} fieldCode={ENROLMENT_FIELD_CODE.CLIENT_SIGNATURE_DATE} errorMessage={getFieldError(ENROLMENT_FIELD_CODE.CLIENT_SIGNATURE_DATE)}><input type="datetime-local" className="wc-input" value={d.clientSignatureDate || ""} onChange={(e) => set("clientSignatureDate", e.target.value)} /></F>
            </Grid>
            <p className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">Return the completed enrolment form along with supporting documentation to the Training Manager Gippsland Institute of Technology.</p>
          </Section>

          {showStaffCompletion && <Section id="staff-completion" title="To be completed by Gippsland Institute of Technology Staff" locked={lockStaffCompletion} onFocus={() => setActive("staff-completion")}>
            <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              <p className="font-semibold text-slate-800">This section is to be completed by Gippsland Institute of Technology staff member assessing the application:</p>
              <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center" data-field-code={ENROLMENT_FIELD_CODE.STAFF_EVIDENCE_SUBMITTED}><span>The client has submitted the appropriate evidence/ documentation in support of the application.</span><YesNoRadio name="staffEvidenceSubmitted" value={d.staffEvidenceSubmitted} disabled={lockStaffCompletion} onChange={(value) => set("staffEvidenceSubmitted", value)} /></div>
              <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center" data-field-code={ENROLMENT_FIELD_CODE.STAFF_ENTRY_REQUIREMENTS_SATISFIED}><span>The client satisfies the entry requirements.</span><YesNoRadio name="staffEntryRequirementsSatisfied" value={d.staffEntryRequirementsSatisfied} disabled={lockStaffCompletion} onChange={(value) => set("staffEntryRequirementsSatisfied", value)} /></div>
              <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center" data-field-code={ENROLMENT_FIELD_CODE.STAFF_OUTCOME_NOTIFIED}><span>The client has been notified of the outcome of the application.</span><YesNoRadio name="staffOutcomeNotified" value={d.staffOutcomeNotified} disabled={lockStaffCompletion} onChange={(value) => set("staffOutcomeNotified", value)} /></div>
            </div>
            <Grid className="mt-4">
              <F label="Staff name" fieldCode={ENROLMENT_FIELD_CODE.STAFF_NAME}><input className="wc-input" value={d.staffName || ""} onChange={(e) => set("staffName", e.target.value)} /></F>
              <F label="Staff signature" fieldCode={ENROLMENT_FIELD_CODE.STAFF_SIGNATURE}>
                <SignaturePad
                  value={d.staffSignatureDataUrl || ""}
                  existingImageUrl={d.staffSignatureViewUrl || ""}
                  disabled={lockStaffCompletion}
                  onChange={(value) => {
                    set("staffSignatureDataUrl", value);
                    set("staffSignatureText", value ? "Signed" : "");
                    set("removeStaffSignature", value === "" && Boolean(d.staffSignatureDocumentId));
                  }}
                />
              </F>
              <F label="Date" fieldCode={ENROLMENT_FIELD_CODE.STAFF_SIGNATURE_DATE}><input type="datetime-local" className="wc-input" value={d.staffSignatureDate || ""} onChange={(e) => set("staffSignatureDate", e.target.value)} /></F>
            </Grid>
          </Section>}

          {/* Actions */}
          {showStudentEditActions && <div className="wc-card p-4 sticky bottom-4 z-10 flex flex-col sm:flex-row items-center gap-3 justify-between">
            <div className="text-xs text-slate-500">
              <span className="font-extrabold text-[#EA580C]">{formProgressPercentage}%</span>{" "}
              <span className="font-semibold text-slate-700">{requiredCount.done}</span> of {requiredCount.total} required fields completed
              <div className="mt-1 h-1.5 w-64 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-orange-500 transition-all" style={{ width: `${formProgressPercentage}%` }} />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              <button type="button" onClick={cancel} disabled={formReadOnly} className="text-sm font-semibold text-rose-600 hover:bg-rose-50 border border-rose-200 px-4 py-2.5 rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="fn-cancel"><X className="w-4 h-4" /> Cancel</button>
              <button type="button" onClick={save} disabled={saving || formReadOnly} className="wc-btn-outline text-sm shrink-0 inline-flex items-center gap-1.5" data-testid="fn-save-bottom"><Save className="w-4 h-4" /> {saving ? "Saving..." : "Save progress"}</button>
              <button type="button" onClick={saveAndReturn} disabled={saving || formReadOnly} className="wc-btn-outline text-sm" data-testid="fn-save"><Save className="w-4 h-4" /> {saving ? "Saving..." : "Save & return"}</button>
              {workflowButtons.map((button) => (
                <button key={workflowButtonKey(button)} type="button" disabled={!canSubmit || saving || formReadOnly} className={`${workflowButtonClass(button)} disabled:opacity-50 disabled:cursor-not-allowed`} data-testid="fn-workflow" data-next-workflow-status-no={workflowButtonNextStatus(button)} data-workflow-status-no={workflowButtonNextStatus(button)} onClick={(event) => handleWorkflowButton(event, button)}><Send className="w-4 h-4" /> {saving ? "Saving..." : workflowButtonText(button)}</button>
              ))}
            </div>
          </div>}
        </form>
      </div>

      {adminReview && workflowButtons.length > 0 && (
        <EnrolmentAdminActionFooter
          buttons={workflowButtons}
          disabled={saving}
          requiredCount={requiredCount}
          formProgressPercentage={formProgressPercentage}
          getButtonKey={workflowButtonKey}
          getButtonText={workflowButtonText}
          getButtonNextStatus={workflowButtonNextStatus}
          getButtonClass={workflowButtonClass}
          onAction={handleWorkflowButton}
        />
      )}

      {submitValidation && (
        <EnrolmentValidationModal
          validation={submitValidation}
          onClose={handleValidationClose}
          onSelectField={handleValidationFieldClick}
        />
      )}

      {directWorkflowAction && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl">
            <div className="border-b border-slate-200 px-6 py-4">
              <h2 className="font-display text-xl font-extrabold text-[#0A2540]">{directWorkflowAction.action} enrolment</h2>
              <p className="mt-1 text-sm text-slate-500">Add a remark before submitting this workflow action.</p>
            </div>
            <div className="space-y-2 px-6 py-5">
              <label className="wc-label">Remark</label>
              <textarea
                className="wc-input min-h-28 resize-y"
                value={directWorkflowAction.comments}
                onChange={(event) => setDirectWorkflowAction((value) => ({ ...value, comments: event.target.value }))}
                placeholder="Enter remarks for this action"
              />
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 px-6 py-4">
              <button type="button" className="wc-btn-outline" disabled={saving} onClick={() => setDirectWorkflowAction(null)}>Cancel</button>
              <button type="button" className="wc-btn-primary" disabled={saving} onClick={confirmDirectWorkflowAction}>{saving ? "Submitting..." : "Submit"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EnrolmentValidationModal({ validation, onClose, onSelectField }) {
  const sections = useMemo(() => [...(validation?.sections || validation?.Sections || [])].sort((left, right) => {
    const leftId = SECTION_CODE_TO_ID[Number(left?.sectionCode ?? left?.SectionCode ?? 0)];
    const rightId = SECTION_CODE_TO_ID[Number(right?.sectionCode ?? right?.SectionCode ?? 0)];
    const leftOrder = SECTIONS.findIndex((section) => section.id === leftId);
    const rightOrder = SECTIONS.findIndex((section) => section.id === rightId);
    return (leftOrder < 0 ? Number.MAX_SAFE_INTEGER : leftOrder) - (rightOrder < 0 ? Number.MAX_SAFE_INTEGER : rightOrder);
  }), [validation]);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    const nextExpandedSections = {};
    sections.forEach((section) => {
      const sectionCode = Number(section?.sectionCode ?? section?.SectionCode ?? 0);
      if (sectionCode > 0) {
        nextExpandedSections[sectionCode] = true;
      }
    });
    setExpandedSections(nextExpandedSections);
  }, [sections]);

  const toggleSection = useCallback((sectionCode) => {
    setExpandedSections((current) => ({
      ...current,
      [sectionCode]: current[sectionCode] === false,
    }));
  }, []);

  const getFieldSortOrder = useCallback((sectionCode, fieldCode) => {
    const orderedFields = VALIDATION_FIELD_ORDER[sectionCode] || [];
    const index = orderedFields.indexOf(fieldCode);
    return index >= 0 ? index : orderedFields.length + fieldCode;
  }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/45 px-4 py-6">
      <div className="w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-display font-extrabold text-[#0A2540]">Enrolment Validation</h2>
            <p className="mt-1 text-sm text-slate-500">{validation?.message || validation?.Message || "Complete the required sections before submitting."}</p>
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50" aria-label="Close enrolment validation">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[70vh] space-y-4 overflow-y-auto p-5">
          {sections.map((section, sectionIndex) => {
            const sectionCode = Number(section?.sectionCode ?? section?.SectionCode ?? 0);
            const sectionName = section?.sectionName || section?.SectionName || `Section ${sectionIndex + 1}`;
            const fields = [...(section?.fields || section?.Fields || [])].sort((left, right) => {
              const leftCode = Number(left?.fieldCode ?? left?.FieldCode ?? 0);
              const rightCode = Number(right?.fieldCode ?? right?.FieldCode ?? 0);
              return getFieldSortOrder(sectionCode, leftCode) - getFieldSortOrder(sectionCode, rightCode);
            });
            const issueCount = Number(section?.issueCount ?? section?.IssueCount ?? fields.length);
            const isExpanded = expandedSections[sectionCode] !== false;
            return (
              <div key={`${sectionCode}-${sectionName}`} className="overflow-hidden rounded-lg border border-slate-200">
                <button type="button" className="flex w-full items-center justify-between gap-4 bg-slate-50 px-4 py-3 text-left" onClick={() => toggleSection(sectionCode)} aria-expanded={isExpanded}>
                  <div className="flex min-w-0 items-center gap-3">
                    <ChevronRight className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                    <span className="text-sm font-extrabold text-[#0A2540]">{sectionName}</span>
                  </div>
                  <span className={`wc-badge border ${getStatusStyleClass(section)}`}>Action Required ({issueCount})</span>
                </button>
                <div className={`${isExpanded ? "divide-y divide-slate-100" : "hidden"}`}>
                  {fields.map((field, fieldIndex) => {
                    const fieldCode = Number(field?.fieldCode ?? field?.FieldCode ?? 0);
                    const fieldName = field?.fieldName || field?.FieldName || `Field ${fieldIndex + 1}`;
                    const message = field?.message || field?.Message || "Required";
                    return (
                      <button key={`${fieldCode}-${fieldName}-${fieldIndex}`} type="button" className="flex w-full items-start justify-between gap-4 px-4 py-3 text-left hover:bg-amber-50/40" onClick={() => onSelectField(sectionCode, fieldCode)}>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-[#0A2540]">{fieldName}</div>
                          <div className="mt-1 text-xs text-slate-500">{message}</div>
                        </div>
                        <span className="wc-badge shrink-0 border bg-amber-50 text-amber-700 border-amber-200">Required</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-end border-t border-slate-200 px-5 py-4">
          <button type="button" onClick={onClose} className="wc-btn-primary">OK</button>
        </div>
      </div>
    </div>
  );
}

function EnrolmentAdminActionFooter({ buttons, disabled, requiredCount, formProgressPercentage, getButtonKey, getButtonText, getButtonNextStatus, getButtonClass, onAction }) {
  return (
    <div className="fixed bottom-0 right-0 left-64 z-40 border-t border-slate-200 bg-white/95 py-3 shadow-[0_-10px_24px_-18px_rgba(15,23,42,0.55)] backdrop-blur max-lg:left-0" data-testid="admin-actions">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 sm:px-6">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <div className="flex min-h-11 items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4">
            <span className="text-xs font-extrabold uppercase tracking-wide text-[#183050]">Workflow Actions</span>
            <span className="h-5 w-px bg-slate-200" />
            <span className="whitespace-nowrap text-sm font-semibold text-slate-600">
              Required: <span className="font-extrabold text-[#183050]">{requiredCount.done}</span> of {requiredCount.total}
            </span>
            <span className="whitespace-nowrap text-sm font-semibold text-slate-600">
              Complete: <span className="font-extrabold text-[#183050]">{formProgressPercentage}%</span>
            </span>
          </div>
        </div>
        <div className="flex max-w-full flex-wrap items-center justify-end gap-2 overflow-x-auto pb-0.5">
          {buttons.map((button) => (
            <button
              key={getButtonKey(button)}
              type="button"
              disabled={disabled}
              data-action={getButtonText(button)}
              data-next-workflow-status-no={getButtonNextStatus(button)}
              data-workflow-status-no={getButtonNextStatus(button)}
              className={`${getButtonClass(button)} disabled:cursor-not-allowed disabled:opacity-60`}
              onClick={(event) => onAction(event, button)}
            >
              {disabled ? "Working..." : getButtonText(button)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function disableLockedControls(children, locked) {
  if (!locked) return children;
  return Children.map(children, (child) => {
    if (!isValidElement(child)) return child;
    const elementType = typeof child.type === "string" ? child.type : "";
    const shouldDisable = elementType === "input" || elementType === "select" || elementType === "textarea";
    const nextProps = {};
    if (shouldDisable) nextProps.disabled = true;
    if (child.props.children) nextProps.children = disableLockedControls(child.props.children, locked);
    return Object.keys(nextProps).length > 0 ? cloneElement(child, nextProps) : child;
  });
}

function Section({ id, title, children, onFocus, merge = false, hidden = false, order, locked = false }) {
  if (hidden) return null;
  const renderedChildren = disableLockedControls(children, locked);
  return (
    <section id={`sec-${id}`} onFocus={onFocus} className={merge ? "pb-6 scroll-mt-24" : "wc-card p-6 scroll-mt-24"} style={order ? { order } : undefined} data-testid={`section-${id}`}>
      {!merge && <div className="flex items-center gap-2 pb-4 mb-5 border-b border-slate-200">
        <div className="w-1 h-6 bg-orange-500 rounded-full" />
        <h2 className="text-lg font-display font-extrabold text-[#0A2540]">{title}</h2>
        {locked && <span className="ml-auto rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-500">Read-only</span>}
      </div>}
      {renderedChildren}
    </section>
  );
}
function Grid({ children, className = "" }) { return <div className={`grid sm:grid-cols-2 gap-4 ${className}`}>{children}</div>; }
function F({ label, children, hint, full = false, hidden = false, order, fieldCode, errorMessage = "" }) {
  if (hidden) return null;
  return (
    <div className={`${full ? "sm:col-span-2" : ""} ${errorMessage ? "rounded-lg border border-red-300 bg-red-50/70 p-3" : ""}`} style={order ? { order } : undefined} data-field-code={fieldCode || undefined} tabIndex={errorMessage ? -1 : undefined}>
      <label className={`wc-label flex items-center gap-1.5 ${errorMessage ? "text-red-800" : ""}`}>{label}{hint && <span className="text-slate-400 font-normal text-xs" title={hint}><Info className="w-3.5 h-3.5 inline" /></span>}</label>
      {children}
      {errorMessage && <div className="mt-1 text-xs font-semibold text-red-700">{errorMessage}</div>}
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}
function YesNoRadio({ name, value, onChange, disabled = false }) {
  return <div className="flex gap-4">{YES_NO_OPTIONS.map((option) => { const selected = isSelectedYesNo(value, option.id); return <label key={option.id} className={`inline-flex items-center gap-2 text-sm ${selected ? "font-semibold text-[#0A2540]" : "text-slate-500"}`}><input type="radio" name={name} checked={selected} disabled={disabled} onChange={() => onChange(option.id)} />{option.name}</label>; })}</div>;
}

function StatusProgress({ status }) {
  const numericStatus = Number(status || 1);
  const meta = getStatusMeta(numericStatus);
  const activeIndex = Math.max(0, EnrollmentStatusTimeline.findIndex((stage) => stage.statuses.includes(numericStatus)));
  return (
    <div className="mt-4 space-y-2" data-testid="enrolment-status-progress">
      <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${meta.className}`}>{meta.label}</span>
      <div className="flex max-w-2xl gap-1" aria-label={`Current enrolment status: ${meta.label}`}>
        {EnrollmentStatusTimeline.map((stage, index) => (
          <div key={stage.label} className="min-w-0 flex-1">
            <div className={`h-1.5 rounded-full ${index <= activeIndex ? "bg-orange-500" : "bg-slate-200"}`} />
            <div className="mt-1 truncate text-[10px] text-slate-500">{stage.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function clearValidationField(validation, fieldCode) {
  if (!validation || !fieldCode) return validation;
  const sections = (validation.sections || validation.Sections || [])
    .map((section) => {
      const fields = (section.fields || section.Fields || []).filter((field) => Number(field?.fieldCode ?? field?.FieldCode ?? 0) !== fieldCode);
      return {
        ...section,
        fields,
        Fields: fields,
        issueCount: fields.length,
        IssueCount: fields.length,
      };
    })
    .filter((section) => Number(section?.issueCount ?? section?.IssueCount ?? 0) > 0);
  if (sections.length === 0) return null;
  return { ...validation, sections, Sections: sections };
}

function getStatusStyleClass(source = {}) {
  const styleNo = Number(source?.statusStyleNo ?? source?.StatusStyleNo ?? 0);
  const style = String(source?.statusStyle || source?.StatusStyle || "").trim();
  if (style === STATUS_STYLE.Completed || styleNo === 1) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (style === STATUS_STYLE.Pending || styleNo === 2) return "bg-amber-50 text-amber-700 border-amber-200";
  if (style === STATUS_STYLE.Locked || styleNo === 4) return "bg-slate-100 text-slate-500 border-slate-200";
  if (style === STATUS_STYLE.Error || styleNo === 5) return "bg-rose-50 text-rose-700 border-rose-200";
  return "bg-orange-50 text-orange-700 border-orange-200";
}

